import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];

  const messages = {
    "footer.poweredby.text": {
      id: "footer.poweredby.text",
      defaultMessage: "Powered by",
      description: "text for the footer",
    },
    "footer.afrolms.altText": {
      id: "footer.afrolms.altText",
      defaultMessage: "Runs on AfroLMS",
      description: "alt text for the footer afroLMS logo",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `Copyrights ©${new Date().getFullYear()}. All Rights Reserved.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>{intl.formatMessage(messages["footer.poweredby.text"])}</li>
              <li>
                <a
                  href="https://afrolms.com"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/afrolms-logo.png`}
                    alt={intl.formatMessage(
                      messages["footer.afrolms.altText"]
                    )}
                    width="79"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {indigoFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a href={`${config.LMS_BASE_URL}${link.url}`}>{link.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
      </footer>
    </div>
  );
};
const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt="Open edX" />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt="Open edX" />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = 'selected-paragon-theme-variant';
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCookie(themeCookie) === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    window.localStorage.setItem(themeCookie, theme);
    setTimeout(() => {
      setCookie(themeCookie, theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    if (!getCookie(themeCookie) || getCookie(themeCookie) === 'undefined') {
      return;
    }
    if (getCookie(themeCookie) !== window.localStorage.getItem(themeCookie)) {
      window.localStorage.setItem(themeCookie, getCookie(themeCookie));
      window.location.reload();
    }
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};
    if (process.env.APP_ID == 'admin-console') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authn') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'authoring') {const { ConfigurableAIAssistance, AIExtensionsCard } = await import("@openedx/openedx-ai-extensions-ui");
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'ai-assist-button-course-outline-sidebar',
                priority: 60,
                type: DIRECT_PLUGIN,
                RenderWidget: ConfigurableAIAssistance,
            },
          }]);
      addPlugins(config, 'org.openedx.frontend.authoring.additional_course_plugin.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'ai-extensions-settings-card',
                    type: DIRECT_PLUGIN,
                    RenderWidget: AIExtensionsCard
                },
            }]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'account') {
// DARACORP_ACCOUNT_V2_LAYOUT_EXTERNAL_LINKS_2026
(() => {
  const build = 'DARACORP_ACCOUNT_V2_LAYOUT_EXTERNAL_LINKS_2026';
  const logo = 'https://learn.daracorp.io/media/afrolms/daracorp_logo.png';
  const styleText = `
    :root { --dca-navy:#062957; --dca-deep:#031d42; --dca-blue:#0e5ce6; --dca-gold:#ffbd2e; --dca-ink:#09264f; --dca-muted:#65748c; --dca-line:#dbe3ed; --dca-canvas:#f5f8fc; }
    body { background:var(--dca-canvas)!important; color:var(--dca-ink); }
    #footer.tutor-container { display:none!important; }
    .site-header-desktop, .site-header-mobile { position:relative; z-index:30; background:#fff!important; border-bottom:1px solid #e1e7ee!important; box-shadow:0 2px 9px rgba(7,35,73,.05); }
    .site-header-desktop { min-height:76px; }
    .site-header-desktop img, .site-header-mobile img { width:auto!important; max-width:170px!important; height:auto!important; max-height:46px!important; object-fit:contain!important; }
    main#main { padding-bottom:70px; }
    .dca-hero { position:relative; overflow:hidden; display:flex; width:min(100% - 48px,1320px); min-height:170px; align-items:center; justify-content:space-between; gap:35px; margin:28px auto 26px; padding:34px 42px; color:#fff; background:linear-gradient(112deg,#052a58,#0b50c5); border-radius:14px; box-shadow:0 16px 38px rgba(5,43,93,.15); }
    .dca-hero::after { content:''; position:absolute; right:-90px; width:330px; height:330px; border:1px solid rgba(255,255,255,.14); border-radius:50%; box-shadow:0 0 0 34px rgba(255,255,255,.035),0 0 0 70px rgba(255,255,255,.022); }
    .dca-hero > div { position:relative; z-index:1; }
    .dca-kicker { display:block; margin-bottom:8px; color:var(--dca-gold); font-size:12px; font-weight:850; letter-spacing:.12em; text-transform:uppercase; }
    .dca-hero h1 { margin:0 0 8px!important; color:#fff!important; font-size:34px!important; font-weight:780!important; letter-spacing:-.02em; }
    .dca-hero p { margin:0!important; color:rgba(255,255,255,.86)!important; font-size:15px!important; }
    .dca-hero a { position:relative; z-index:2; display:inline-flex; min-height:44px; align-items:center; padding:0 18px; color:#08244d!important; background:var(--dca-gold); border-radius:7px; font-weight:800; text-decoration:none!important; }
    .page__account-settings { display:block!important; width:min(100% - 48px,1320px)!important; max-width:none!important; margin:0 auto!important; padding:0!important; }
    .page__account-settings > h1 { position:absolute!important; width:1px!important; height:1px!important; overflow:hidden!important; clip:rect(0 0 0 0)!important; }
    .page__account-settings > div > .row { display:grid!important; grid-template-columns:245px minmax(0,1fr)!important; align-items:start; gap:32px!important; margin:0!important; }
    .page__account-settings > div > .row > [class*='col-'] { width:auto!important; max-width:none!important; min-width:0!important; flex:none!important; padding:0!important; }
    .page__account-settings .jump-nav { position:sticky!important; top:22px!important; padding:18px!important; background:#fff; border:1px solid var(--dca-line); border-radius:11px; box-shadow:0 7px 22px rgba(8,40,82,.06); }
    .page__account-settings .jump-nav li + li { margin-top:4px; }
    .page__account-settings .jump-nav a { display:block; padding:9px 10px; border-radius:6px; color:#37506f!important; font-weight:650; text-decoration:none!important; }
    .page__account-settings .jump-nav a:hover, .page__account-settings .jump-nav a:focus { color:#074fac!important; background:#eef5ff; }
    .page__account-settings .account-section { min-width:0; margin:0 0 30px!important; padding:24px!important; background:#fff; border:1px solid var(--dca-line); border-radius:11px; box-shadow:0 7px 22px rgba(8,40,82,.05); }
    .page__account-settings h2 { margin:28px 0 13px!important; color:var(--dca-ink)!important; font-size:21px!important; font-weight:780!important; }
    .page__account-settings h2:first-child { margin-top:0!important; }
    .page__account-settings .card, .page__account-settings [class*='form-group'], .page__account-settings [class*='account-settings'] section > div { border-color:var(--dca-line)!important; border-radius:9px!important; box-shadow:none!important; }
    .page__account-settings .card { margin-bottom:12px!important; background:#fff!important; }
    .page__account-settings .btn-primary { color:#fff!important; background:var(--dca-blue)!important; border-color:var(--dca-blue)!important; border-radius:6px!important; }
    .page__account-settings a { color:#0759c8; }
    .dca-footer { color:#fff; background:var(--dca-deep); border-top:3px solid var(--dca-gold); }
    .dca-footer-main { display:grid; width:min(100% - 64px,1480px); grid-template-columns:minmax(300px,1.4fr) repeat(3,minmax(145px,.55fr)); gap:50px; margin:0 auto; padding:48px 0 36px; }
    .dca-footer-logo { display:inline-flex; padding:8px 12px; background:#fff; border-radius:8px; }
    .dca-footer-logo img { display:block; width:auto; max-width:170px; max-height:46px; }
    .dca-footer-brand p { max-width:390px; margin:17px 0 13px; color:#bdcce0; font-size:14px; line-height:1.65; }
    .dca-footer-social { display:flex; flex-wrap:wrap; gap:14px; }
    .dca-footer a { color:#c8d6e7!important; font-size:13px; text-decoration:none!important; }
    .dca-footer a:hover { color:var(--dca-gold)!important; }
    .dca-footer-column { display:flex; align-items:flex-start; flex-direction:column; gap:10px; }
    .dca-footer-column h2 { margin:3px 0 8px; color:var(--dca-gold); font-size:13px; letter-spacing:.08em; text-transform:uppercase; }
    .dca-footer-column p { margin:0 0 8px; color:#bdcce0; font-size:13px; line-height:1.55; }
    .dca-footer a.dca-demo { display:inline-flex; min-height:40px; align-items:center; padding:0 15px; color:#061f4e!important; background:#ffbd2e!important; border-radius:6px; box-shadow:none!important; font-weight:850; opacity:1; text-shadow:none!important; }
    .dca-footer-bottom { display:flex; width:min(100% - 64px,1480px); min-height:64px; align-items:center; justify-content:space-between; gap:20px; margin:0 auto; color:#aebfd5; border-top:1px solid rgba(255,255,255,.12); font-size:12px; }
    @media (max-width:850px) { .page__account-settings > div > .row { grid-template-columns:1fr!important; } .page__account-settings .jump-nav { position:relative!important; top:auto!important; } .dca-footer-main { grid-template-columns:1fr 1fr; } }
    @media (max-width:620px) { .dca-hero { width:calc(100% - 28px); align-items:flex-start; flex-direction:column; padding:28px 24px; } .page__account-settings { width:calc(100% - 28px)!important; } }
    .dca-footer { display:flex; min-height:112px; align-items:center; justify-content:center; color:inherit; background:linear-gradient(115deg,#031d42 0%,#062957 68%,#0b49a6 100%); border-top:3px solid var(--dca-gold); }
    .dca-footer-main { display:flex; width:auto; align-items:center; justify-content:center; margin:0; padding:22px; }
    .dca-footer-main > :not(.dca-footer-brand), .dca-footer-brand > p, .dca-footer-social, .dca-footer-bottom { display:none!important; }
    .dca-footer-logo { padding:8px 12px; background:#fff; box-shadow:0 10px 24px rgba(0,0,0,.16); }
    .dca-footer-logo img { max-width:148px; max-height:42px; }
  `;

  const footerMarkup = `
    <div class="dca-footer-main">
      <div class="dca-footer-brand"><a class="dca-footer-logo" href="https://daracorp.io/" target="_blank" rel="noopener noreferrer"><img src="${logo}" alt="Daracorp"></a><p>Practical compliance learning built for confident decisions, stronger teams, and lasting growth.</p><div class="dca-footer-social"><a href="https://www.linkedin.com/company/daracorp" target="_blank" rel="noopener noreferrer">LinkedIn</a><a href="https://www.instagram.com/daracorpethioadvise" target="_blank" rel="noopener noreferrer">Instagram</a><a href="https://x.com/DaraCorpEthioAd" target="_blank" rel="noopener noreferrer">X</a></div></div>
      <nav class="dca-footer-column" aria-label="Explore"><h2>Explore</h2><a href="https://learn.daracorp.io/courses">Courses</a><a href="https://learn.daracorp.io/courses?view=learning-paths">Learning paths</a><a href="https://daracorp.io/why" target="_blank" rel="noopener noreferrer">Why Daracorp</a><a href="https://daracorp.io/contact" target="_blank" rel="noopener noreferrer">Contact</a></nav>
      <nav class="dca-footer-column" aria-label="Legal"><h2>Legal</h2><a href="https://daracorp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy</a><a href="https://daracorp.io/terms" target="_blank" rel="noopener noreferrer">Terms</a><a href="https://daracorp.io/security" target="_blank" rel="noopener noreferrer">Security</a></nav>
      <div class="dca-footer-column"><h2>Get started</h2><p>See Daracorp in action for your team.</p><a class="dca-demo" href="https://daracorp.io/demo" target="_blank" rel="noopener noreferrer">Request a demo</a></div>
    </div><div class="dca-footer-bottom"><span>© 2026 Daracorp. All rights reserved.</span><a href="https://daracorp.io/" target="_blank" rel="noopener noreferrer">daracorp.io</a></div>`;

  const enhance = () => {
    if (!document.getElementById('daracorp-account-v2-style')) {
      document.getElementById('daracorp-account-v1-style')?.remove();
      const style = document.createElement('style'); style.id = 'daracorp-account-v2-style'; style.dataset.build = build; style.textContent = styleText; document.head.appendChild(style);
    }
    document.querySelectorAll('header img').forEach(image => {
      const alt = (image.getAttribute('alt') || '').toLowerCase();
      if (alt.includes('open edx') || alt.includes('openedx') || alt.includes('logo')) { image.src = logo; image.alt = 'Daracorp'; }
    });
    const headerLogo = document.querySelector('header a img[src="' + logo + '"]')?.closest('a');
    if (headerLogo) headerLogo.href = 'https://apps.learn.daracorp.io/learner-dashboard/';
    const page = document.querySelector('.page__account-settings');
    if (page && !document.querySelector('.dca-hero')) {
      const hero = document.createElement('section'); hero.className = 'dca-hero'; hero.setAttribute('aria-labelledby','dca-title');
      hero.innerHTML = '<div><span class="dca-kicker">Your profile</span><h1 id="dca-title">Account &amp; profile</h1><p>Manage your personal information, password, preferences, and connected accounts.</p></div><a href="https://apps.learn.daracorp.io/learner-dashboard/">Back to My Learning</a>';
      page.parentNode.insertBefore(hero,page);
    }
    if (page && !document.querySelector('.dca-footer')) {
      const footer = document.createElement('footer'); footer.className = 'dca-footer'; footer.setAttribute('aria-label','Daracorp footer'); footer.innerHTML = footerMarkup; document.body.appendChild(footer);
    }
  };
  enhance();
  new MutationObserver(enhance).observe(document.documentElement,{ childList:true, subtree:true });
})();
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'communications') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'gradebook') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learner-dashboard') {
// DARACORP_DASHBOARD_V7_CERTIFICATE_API_ACCOUNT_ADMIN_2026
const dcReactModule = await import('react');
const DCReact = dcReactModule.default;
const { createPortal: dcCreatePortal } = await import('react-dom');
const {
  getAuthenticatedUser: dcGetAuthenticatedUser,
  getAuthenticatedHttpClient: dcGetAuthenticatedHttpClient,
} = await import('@edx/frontend-platform/auth');
const { getConfig: dcGetConfig } = await import('@edx/frontend-platform');
const { Pagination: DCPagination } = await import('@openedx/paragon');
const { ActiveCourseFilters: DCActiveCourseFilters } = await import('containers/CourseFilterControls');
const { reduxHooks: dcDashboardReduxHooks } = await import('hooks');

const DC_LOGO_URL = 'https://learn.daracorp.io/media/afrolms/daracorp_logo.png';
const DC_DASHBOARD_BUILD = 'DARACORP_DASHBOARD_V7_CERTIFICATE_API_ACCOUNT_ADMIN_2026';
const dcProgressCache = new Map();
const dcProgressPending = new Map();

const dcAbsoluteUrl = (url) => {
  if (!url) return '#';
  if (/^https?:\/\//i.test(url)) return url;
  return `${dcGetConfig().LMS_BASE_URL || 'https://learn.daracorp.io'}${url.startsWith('/') ? '' : '/'}${url}`;
};

const dcCourseImageUrl = (url) => dcAbsoluteUrl(url || '/media/afrolms/daracorp_logo.png');

const dcLoadProgress = (courseId) => {
  if (!courseId) return Promise.resolve({ percent: 0, nextUp: '', loaded: true });
  if (dcProgressCache.has(courseId)) return Promise.resolve(dcProgressCache.get(courseId));
  if (dcProgressPending.has(courseId)) return dcProgressPending.get(courseId);

  const request = dcGetAuthenticatedHttpClient()
    .get(`${dcGetConfig().LMS_BASE_URL}/api/course_home/progress/${courseId}`)
    .then(({ data }) => {
      const rawGradeValue = data?.course_grade?.percent;
      const gradeValue = rawGradeValue === null || rawGradeValue === undefined || rawGradeValue === ''
        ? Number.NaN
        : Number(rawGradeValue);
      const summary = data?.completion_summary || {};
      const complete = Number(summary.complete_count || 0);
      const incomplete = Number(summary.incomplete_count || 0);
      const locked = Number(summary.locked_count || 0);
      const total = complete + incomplete + locked;
      const completionValue = total > 0 ? complete / total : 0;
      // Course grade is not course completion. Prefer the completion summary,
      // and only use grade when this response has no completion data.
      const normalized = total > 0 ? completionValue : (Number.isFinite(gradeValue) ? gradeValue : 0);
      const percent = Math.max(0, Math.min(100, Math.round(normalized * 100)));
      let nextUp = '';
      (data?.section_scores || []).some(section => (section.subsections || []).some(subsection => {
        const subsectionPercent = Number(subsection.percent_graded || 0);
        if (subsectionPercent < 1 && subsection.display_name) {
          nextUp = subsection.display_name;
          return true;
        }
        return false;
      }));
      const result = { percent, nextUp, loaded: true };
      dcProgressCache.set(courseId, result);
      return result;
    })
    .catch(() => {
      const result = { percent: 0, nextUp: '', loaded: true };
      dcProgressCache.set(courseId, result);
      return result;
    })
    .finally(() => dcProgressPending.delete(courseId));

  dcProgressPending.set(courseId, request);
  return request;
};

const useDcProgress = (courseId, hasStarted) => {
  const initial = dcProgressCache.get(courseId) || { percent: hasStarted ? null : 0, nextUp: '', loaded: false };
  const [progress, setProgress] = DCReact.useState(initial);
  DCReact.useEffect(() => {
    let active = true;
    dcLoadProgress(courseId).then(value => { if (active) setProgress(value); });
    return () => { active = false; };
  }, [courseId]);
  return progress;
};

const useDcCertificates = () => {
  const learner = dcGetAuthenticatedUser() || {};
  const username = learner.username || '';
  const [state, setState] = DCReact.useState({ records: [], loaded: false });
  DCReact.useEffect(() => {
    let active = true;
    if (!username) {
      setState({ records: [], loaded: true });
      return () => { active = false; };
    }
    const endpoint = `${dcGetConfig().LMS_BASE_URL}/api/certificates/v0/certificates/${encodeURIComponent(username)}/`;
    dcGetAuthenticatedHttpClient().get(endpoint)
      .then(({ data }) => {
        if (!active) return;
        const records = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
        setState({ records: records.filter(record => record?.status === 'downloadable' || record?.is_passing || record?.download_url), loaded: true });
      })
      .catch(() => { if (active) setState({ records: [], loaded: true }); });
    return () => { active = false; };
  }, [username]);
  return state;
};

const useDcDashboardSummary = (courses, certificateRecords) => {
  const activeCourses = DCReact.useMemo(
    () => courses.filter(course => course?.enrollment?.isEnrolled && !course?.courseRun?.isArchived),
    [courses],
  );
  const nativeCertificates = Math.max(
    certificateRecords.length,
    courses.filter(course => course?.certificate?.isEarned).length,
  );
  const [calculated, setCalculated] = DCReact.useState({ averageProgress: 0 });

  DCReact.useEffect(() => {
    let active = true;
    Promise.all(activeCourses.map(course => dcLoadProgress(course?.courseRun?.courseId)))
      .then(values => {
        if (!active) return;
        const validValues = values.filter(value => value.percent !== null && value.percent !== undefined);
        const average = validValues.length
          ? Math.round(validValues.reduce((total, value) => total + Number(value.percent || 0), 0) / validValues.length)
          : 0;
        setCalculated({ averageProgress: average });
      });
    return () => { active = false; };
  }, [activeCourses.map(course => course?.courseRun?.courseId).join('|'), nativeCertificates]);

  return {
    activeCount: activeCourses.length,
    averageProgress: calculated.averageProgress,
    // A completed course is not the same thing as an issued certificate.
    // Show only downloadable/passing certificate API records here.
    certificates: nativeCertificates,
  };
};

const useDcAllCourseData = () => {
  const courseCount = dcDashboardReduxHooks.useCurrentCourseList({
    sortBy: 'enrolled',
    filters: [],
    pageSize: 0,
  });
  return courseCount?.visibleList || courseCount?.visible || [];
};

const DCBookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5z" />
    <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5z" />
  </svg>
);
const DCTrendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="m4 17 5-5 4 3 7-9" /><path d="M15 6h5v5" />
  </svg>
);
const DCAwardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="8" r="5" /><path d="m9 13-1 8 4-2 4 2-1-8" /><path d="m10.2 8 1.2 1.2L14 6.8" />
  </svg>
);
const DCSearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" />
  </svg>
);
const DCBellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" />
  </svg>
);
const DCCalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const DaracorpDesktopHeader = () => {
  const learner = dcGetAuthenticatedUser() || {};
  const displayName = learner.name || learner.username || 'Learner';
  const firstName = displayName.trim().split(/\s+/)[0];
  const initial = firstName.charAt(0).toUpperCase();
  const config = dcGetConfig();
  const [menuOpen, setMenuOpen] = DCReact.useState(false);
  const menuRef = DCReact.useRef(null);
  DCReact.useEffect(() => {
    const closeMenu = event => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && menuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('pointerdown', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, []);
  const dashboardUrl = 'https://apps.learn.daracorp.io/learner-dashboard/';
  const accountUrl = config.ACCOUNT_SETTINGS_URL || 'https://apps.learn.daracorp.io/account/';
  const refreshmentUrl = `${config.LMS_BASE_URL}/courses/course-v1:Daracorp+INV103+2026/refreshment/`;
  const certificateView = new URLSearchParams(window.location.search).get('view') === 'certificates';
  return (
    <header className="dc-site-header dc-site-header-desktop">
      <style data-daracorp-dashboard-v7-header={DC_DASHBOARD_BUILD}>{DARACORP_DASHBOARD_V2_CSS}</style>
      <a className="sr-only sr-only-focusable" href="#main">Skip to main content</a>
      <div className="dc-header-inner">
        <a className="dc-header-logo" href="https://apps.learn.daracorp.io/learner-dashboard/" aria-label="Daracorp learning dashboard">
          <img src={DC_LOGO_URL} alt="Daracorp" width="170" height="48" />
        </a>
        <nav className="dc-primary-nav" aria-label="Main navigation">
          <a className={certificateView ? '' : 'active'} href="/learner-dashboard/">My Learning</a>
          <a href={`${config.LMS_BASE_URL}/courses`}>Explore courses</a>
          <a href={`${config.LMS_BASE_URL}/courses?view=learning-paths`}>Learning paths</a>
          <a href={refreshmentUrl}>Refreshment</a>
          <a className={certificateView ? 'active' : ''} href={`${dashboardUrl}?view=certificates`}>Certificates</a>
        </nav>
        <nav className="dc-utility-nav" aria-label="Learner tools">
          <a className="dc-icon-link" href={`${config.LMS_BASE_URL}/courses`} aria-label="Search courses"><DCSearchIcon /></a>
          <button className="dc-icon-link" type="button" aria-label="Notifications"><DCBellIcon /></button>
          <div className="dc-account-menu" ref={menuRef}>
            <button className="dc-account-link" type="button" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)}>
              <span className="dc-avatar">{initial}</span><span>{firstName}</span><span className="dc-account-chevron" aria-hidden="true">⌄</span>
            </button>
            {menuOpen && (
              <div className="dc-account-dropdown" role="menu">
                <div className="dc-account-identity"><strong>{displayName}</strong><span>{learner.email || learner.username || ''}</span></div>
                <a role="menuitem" href={dashboardUrl}>My learning</a>
                <a role="menuitem" href={`${dashboardUrl}?view=certificates`}>Certificates</a>
                <a role="menuitem" href={accountUrl}>Account settings</a>
                <a role="menuitem" className="dc-sign-out" href={`${config.LMS_BASE_URL}/logout`}>Sign out</a>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

const DaracorpMobileHeader = () => {
  const learner = dcGetAuthenticatedUser() || {};
  const displayName = learner.name || learner.username || 'Learner';
  const initial = displayName.trim().charAt(0).toUpperCase();
  const config = dcGetConfig();
  return (
    <header className="dc-site-header dc-site-header-mobile">
      <style data-daracorp-dashboard-v7-mobile-header={DC_DASHBOARD_BUILD}>{DARACORP_DASHBOARD_V2_CSS}</style>
      <a className="dc-header-logo" href="https://apps.learn.daracorp.io/learner-dashboard/"><img src={DC_LOGO_URL} alt="Daracorp" width="145" height="42" /></a>
      <div className="dc-mobile-tools">
        <a className="dc-icon-link" href={`${config.LMS_BASE_URL}/courses`} aria-label="Discover courses"><DCSearchIcon /></a>
        <a className="dc-avatar" href={config.ACCOUNT_SETTINGS_URL || 'https://apps.learn.daracorp.io/account/'} aria-label={`Account for ${displayName}`}>{initial}</a>
      </div>
    </header>
  );
};

const DCStat = ({ icon, value, label, href }) => {
  const Tag = href ? 'a' : 'div';
  return (
  <Tag className="dc-summary-stat" href={href || undefined}>
    <span className="dc-summary-icon">{icon}</span>
    <strong>{value}</strong>
    <span>{label}</span>
  </Tag>
  );
};

const DaracorpWelcome = ({ summary }) => {
  let learner = {};
  try { learner = dcGetAuthenticatedUser() || {}; } catch (error) { learner = {}; }
  const displayName = learner.name || learner.username || 'Learner';
  const firstName = displayName.trim().split(/\s+/)[0];
  return (
    <section className="dc-dashboard-hero" aria-labelledby="dc-welcome-title">
      <div className="dc-welcome-copy">
        <span className="dc-welcome-kicker">Welcome back</span>
        <h1 id="dc-welcome-title">Keep learning, {firstName}</h1>
        <p>Continue where you left off and keep your momentum going.</p>
      </div>
      <div className="dc-summary-grid" aria-label="Learning summary">
        <DCStat icon={<DCBookIcon />} value={summary.activeCount} label="Active Courses" />
        <DCStat icon={<DCTrendIcon />} value={`${summary.averageProgress}%`} label="Average Progress" />
        <DCStat icon={<DCAwardIcon />} value={summary.certificates} label={summary.certificates === 1 ? 'Certificate Earned' : 'Certificates Earned'} href="/learner-dashboard/?view=certificates" />
      </div>
    </section>
  );
};

const DaracorpCourseCard = ({ cardId, certificateOnly = false }) => {
  const course = dcDashboardReduxHooks.useCardCourseData(cardId) || {};
  const run = dcDashboardReduxHooks.useCardCourseRunData(cardId) || {};
  const enrollment = dcDashboardReduxHooks.useCardEnrollmentData(cardId) || {};
  const provider = dcDashboardReduxHooks.useCardProviderData(cardId) || {};
  const hasStarted = Boolean(enrollment.hasStarted);
  const progress = useDcProgress(run.courseId, hasStarted);
  const percent = progress.percent === null ? 0 : Number(progress.percent || 0);
  const progressStyle = { width: `${percent}%` };
  const actionUrl = dcAbsoluteUrl(hasStarted && run.resumeUrl ? run.resumeUrl : run.homeUrl);
  const nextUp = progress.nextUp || (hasStarted ? 'Continue where you left off' : 'Start the first lesson');
  if (certificateOnly && progress.loaded && percent < 100) return null;
  if (certificateOnly && !progress.loaded) return <div className="dc-certificate-loading" aria-hidden="true" />;
  return (
    <article className="dc-learning-card" aria-labelledby={`${cardId}-title`}>
      <a className="dc-card-visual" href={dcAbsoluteUrl(run.homeUrl)} tabIndex="-1" aria-hidden="true">
        <img src={dcCourseImageUrl(course.bannerImgSrc)} alt="" />
        <span>Foundational</span>
      </a>
      <div className="dc-card-content">
        <div className="dc-card-title-row">
          <h3 id={`${cardId}-title`}><a href={dcAbsoluteUrl(run.homeUrl)}>{course.courseName || 'Course'}</a></h3>
          <span className="dc-card-more" aria-hidden="true">•••</span>
        </div>
        <p className="dc-card-meta">{provider.name || 'Daracorp'} <span>•</span> {course.courseNumber || ''}</p>
        <div className="dc-progress-row">
          <span className="dc-progress-track"><span style={progressStyle} /></span>
          <strong>{hasStarted ? `${percent}%` : 'Not started'}</strong>
        </div>
        <p className="dc-progress-label">{hasStarted ? `${percent}% complete` : 'Ready when you are'}</p>
        <div className="dc-card-footer">
          <span className="dc-next-up"><DCCalendarIcon /><span><strong>Next up:</strong> {nextUp}</span></span>
          <a className="dc-course-action" href={actionUrl}>{hasStarted ? 'Resume course' : 'Begin course'}</a>
        </div>
      </div>
    </article>
  );
};

const DCChallengeIllustration = () => (
  <svg className="dc-challenge-illustration" viewBox="0 0 260 190" role="img" aria-label="Course discovery illustration">
    <defs>
      <linearGradient id="dcChallengeBg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#edf8ff" /><stop offset="1" stopColor="#dceeff" />
      </linearGradient>
      <linearGradient id="dcChallengeScreen" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#0b3265" /><stop offset="1" stopColor="#1262e7" />
      </linearGradient>
    </defs>
    <circle cx="130" cy="92" r="76" fill="url(#dcChallengeBg)" />
    <circle cx="218" cy="40" r="5" fill="#16c4df" /><circle cx="226" cy="70" r="7" fill="#16c4df" />
    <rect x="69" y="45" width="125" height="91" rx="8" fill="url(#dcChallengeScreen)" />
    <rect x="76" y="54" width="111" height="74" rx="4" fill="#fff" />
    <rect x="86" y="64" width="49" height="5" rx="2.5" fill="#c8d8ed" />
    <rect x="86" y="75" width="79" height="4" rx="2" fill="#e0e8f2" />
    <path d="M93 111c12-14 22-6 32-18 10-12 21 14 35-9" fill="none" stroke="#e3a014" strokeWidth="4" strokeLinecap="round" />
    <circle cx="91" cy="108" r="24" fill="#fff" stroke="#0b3265" strokeWidth="5" />
    <circle cx="91" cy="108" r="13" fill="none" stroke="#79a4cf" strokeWidth="3" />
    <path d="m106 125 18 19" stroke="#0b3265" strokeWidth="7" strokeLinecap="round" />
    <rect x="109" y="136" width="46" height="7" rx="3.5" fill="#0b3265" opacity=".18" />
  </svg>
);

const DaracorpChallenge = () => (
  <aside className="dc-challenge-card" aria-labelledby="dc-challenge-title">
    <DCChallengeIllustration />
    <h2 id="dc-challenge-title">Ready for a new challenge?</h2>
    <p>Explore more courses to build your expertise.</p>
    <a href={`${dcGetConfig().LMS_BASE_URL}/courses`}>Explore courses <span aria-hidden="true">→</span></a>
  </aside>
);

const DaracorpCertificatesIntro = ({ count }) => (
  <section className="dc-certificates-intro" aria-labelledby="dc-certificates-title">
    <div>
      <span className="dc-certificates-kicker">Your achievements</span>
      <h2 id="dc-certificates-title">Completed certificates</h2>
      <p>{count > 0 ? `You have ${count} completed ${count === 1 ? 'certificate' : 'certificates'}.` : 'Complete an eligible course to earn your first Daracorp certificate.'}</p>
    </div>
    <a href="/learner-dashboard/">Back to My Learning</a>
  </section>
);

const DaracorpCertificateCard = ({ record }) => {
  const certificateUrl = dcAbsoluteUrl(record?.download_url || record?.certificate_url || record?.url);
  const issuedDate = record?.created_date ? new Date(record.created_date) : null;
  const issued = issuedDate && !Number.isNaN(issuedDate.getTime())
    ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(issuedDate)
    : '';
  const rawGrade = String(record?.grade ?? '').trim();
  const numericGrade = Number(rawGrade.replace('%', ''));
  const grade = Number.isFinite(numericGrade)
    ? `${Math.round(rawGrade.includes('%') || numericGrade > 1 ? numericGrade : numericGrade * 100)}%`
    : '';
  return (
    <article className="dc-certificate-card">
      <div className="dc-certificate-emblem"><DCAwardIcon /></div>
      <div className="dc-certificate-copy">
        <span className="dc-certificate-status">Certificate earned</span>
        <h3>{record?.course_display_name || 'Daracorp course certificate'}</h3>
        <p>{record?.course_organization || 'Daracorp'}{issued ? ` - Issued ${issued}` : ''}{grade ? ` - Grade ${grade}` : ''}</p>
        <span className="dc-certificate-course-id">{record?.course_id || ''}</span>
      </div>
      {certificateUrl !== '#' && <a className="dc-certificate-view" href={certificateUrl} target="_blank" rel="noopener noreferrer">View certificate <span aria-hidden="true">↗</span></a>}
    </article>
  );
};

const DaracorpLogoFooter = () => (
  <footer className="dc-logo-footer" aria-label="Daracorp footer">
    <div className="dc-footer-main">
      <div className="dc-footer-brand-block">
        <a className="dc-footer-logo" href="https://daracorp.io/" target="_blank" rel="noopener noreferrer" aria-label="Daracorp home">
          <img src={DC_LOGO_URL} alt="Daracorp" width="170" height="46" />
        </a>
        <p>Practical compliance learning built for confident decisions, stronger teams, and lasting growth.</p>
        <div className="dc-footer-social"><a href="https://www.linkedin.com/company/daracorp" target="_blank" rel="noopener noreferrer">LinkedIn</a><a href="https://www.instagram.com/daracorpethioadvise" target="_blank" rel="noopener noreferrer">Instagram</a><a href="https://x.com/DaraCorpEthioAd" target="_blank" rel="noopener noreferrer">X</a></div>
      </div>
      <nav className="dc-footer-column" aria-label="Explore Daracorp"><h2>Explore</h2><a href="https://learn.daracorp.io/courses">Courses</a><a href="https://learn.daracorp.io/courses?view=learning-paths">Learning paths</a><a href="https://daracorp.io/why" target="_blank" rel="noopener noreferrer">Why Daracorp</a><a href="https://daracorp.io/contact" target="_blank" rel="noopener noreferrer">Contact</a></nav>
      <nav className="dc-footer-column" aria-label="Legal"><h2>Legal</h2><a href="https://daracorp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy</a><a href="https://daracorp.io/terms" target="_blank" rel="noopener noreferrer">Terms</a><a href="https://daracorp.io/security" target="_blank" rel="noopener noreferrer">Security</a></nav>
      <div className="dc-footer-column dc-footer-cta"><h2>Get started</h2><p>See Daracorp in action for your team.</p><a className="dc-footer-demo" href="https://daracorp.io/demo" target="_blank" rel="noopener noreferrer">Request a demo</a></div>
    </div>
    <div className="dc-footer-bottom"><span>© 2026 Daracorp. All rights reserved.</span><a href="https://daracorp.io/" target="_blank" rel="noopener noreferrer">daracorp.io</a></div>
  </footer>
);

const DaracorpMinimalLogoFooter = () => (
  <footer className="dc-logo-footer-minimal" aria-label="Daracorp footer">
    <a href="https://daracorp.io/" target="_blank" rel="noopener noreferrer" aria-label="Daracorp home"><img src={DC_LOGO_URL} alt="Daracorp" width="148" height="42" /></a>
  </footer>
);

const DARACORP_DASHBOARD_V2_CSS = `
  /* DARACORP_DASHBOARD_V8_APPROVED_FULL_WIDTH_2026 */
  :root {
    --dc2-navy:#062957; --dc2-navy-deep:#031d42; --dc2-blue:#0e5ce6;
    --dc2-blue-bright:#1c66ed; --dc2-gold:#ffbd2e; --dc2-text:#09264f;
    --dc2-muted:#64738b; --dc2-line:#dfe5ed; --dc2-canvas:#f7f9fc;
  }
  body, #root { background:var(--dc2-canvas); color:var(--dc2-text); }
  #footer.tutor-container { display:none!important; }
  .dc-site-header { position:relative; z-index:1000; background:#fff; border-bottom:1px solid #e3e7ed; box-shadow:0 2px 10px rgba(8,36,78,.04); }
  .dc-header-inner { display:flex; align-items:stretch; width:min(100% - 64px,1540px); min-height:78px; margin:0 auto; }
  .dc-header-logo { display:flex; width:176px; align-items:center; flex:0 0 auto; margin-right:0; }
  .dc-header-logo img { display:block; width:auto; max-width:170px; max-height:48px; object-fit:contain; }
  .dc-primary-nav { display:grid; grid-template-columns:118px 146px 142px 126px 116px; align-items:stretch; column-gap:6px; margin-left:auto; }
  .dc-primary-nav a { position:relative; display:flex; min-width:0; align-items:center; justify-content:center; padding:0 8px; color:#34445f; font-weight:650; text-align:center; text-decoration:none; white-space:nowrap; }
  .dc-primary-nav a.active { color:var(--dc2-blue); }
  .dc-primary-nav a.active::after { content:''; position:absolute; right:0; bottom:0; left:0; height:3px; border-radius:3px 3px 0 0; background:var(--dc2-blue); }
  .dc-utility-nav { display:flex; align-items:center; gap:20px; margin-left:22px; }
  .dc-icon-link { display:grid; width:42px; height:42px; place-items:center; padding:0; color:var(--dc2-text); background:transparent; border:0; border-radius:50%; }
  .dc-icon-link:hover { color:var(--dc2-blue); background:#f1f5fb; }
  .dc-icon-link svg { width:24px; height:24px; }
  .dc-account-menu { position:relative; }
  .dc-account-link { display:flex; align-items:center; gap:11px; padding:0; color:var(--dc2-text)!important; background:transparent; border:0; font-weight:700; text-decoration:none!important; cursor:pointer; }
  .dc-account-chevron { transition:transform .18s ease; }
  .dc-account-link[aria-expanded='true'] .dc-account-chevron { transform:rotate(180deg); }
  .dc-account-dropdown { position:absolute; top:calc(100% + 13px); right:0; width:245px; overflow:hidden; padding:8px; background:#fff; border:1px solid #dce4ed; border-radius:10px; box-shadow:0 18px 45px rgba(5,35,75,.18); }
  .dc-account-dropdown::before { content:''; position:absolute; top:-6px; right:25px; width:11px; height:11px; background:#fff; border-top:1px solid #dce4ed; border-left:1px solid #dce4ed; transform:rotate(45deg); }
  .dc-account-identity { display:flex; flex-direction:column; gap:3px; margin:0 4px 6px; padding:10px 10px 13px; border-bottom:1px solid #e6ebf1; }
  .dc-account-identity strong { color:var(--dc2-text); font-size:14px; }
  .dc-account-identity span { overflow:hidden; color:var(--dc2-muted); font-size:12px; text-overflow:ellipsis; white-space:nowrap; }
  .dc-account-dropdown > a { display:flex; min-height:40px; align-items:center; padding:0 11px; color:#243d61!important; border-radius:6px; font-size:13px; font-weight:650; text-decoration:none!important; }
  .dc-account-dropdown > a:hover { color:var(--dc2-blue)!important; background:#eef5ff; }
  .dc-account-dropdown > a.dc-sign-out { margin-top:5px; color:#a72b2b!important; border-top:1px solid #edf0f4; border-radius:0 0 6px 6px; }
  .dc-avatar { display:grid; width:42px; height:42px; place-items:center; flex:0 0 auto; color:#fff!important; background:var(--dc2-blue); border-radius:50%; font-weight:800; text-decoration:none!important; }
  .dc-site-header-mobile { display:none; min-height:64px; align-items:center; justify-content:space-between; padding:8px 18px; }
  .dc-site-header-mobile .dc-header-logo { width:auto; margin:0; }
  .dc-site-header-mobile .dc-header-logo img { max-width:145px; max-height:42px; }
  .dc-mobile-tools { display:flex; align-items:center; gap:8px; }

  main .container-xl { max-width:1540px; }
  #dashboard-content { display:flex; flex-direction:column; }
  #dashboard-content > .container-mw-xl { order:0; width:100%; max-width:1540px; }
  #dashboard-content .course-list-column { width:100%!important; max-width:100%!important; flex:0 0 100%!important; }
  #dashboard-content .sidebar-column,
  #looking-for-challenge-widget { display:none!important; }
  .course-list-container { display:flex; flex-direction:column; }
  .dc-dashboard-hero { order:-2; position:relative; isolation:isolate; overflow:hidden; display:grid; width:min(calc(100% - 32px),1540px); grid-template-columns:minmax(0,1fr) minmax(620px,1fr); align-items:center; gap:42px; min-height:196px; margin:20px auto 32px; padding:40px 52px; color:#fff; background:linear-gradient(110deg,#052a58 0%,#073b83 48%,#115ce0 100%); border-radius:14px; box-shadow:0 15px 38px rgba(4,43,101,.16); }
  .dc-dashboard-hero::after { content:''; position:absolute; z-index:-1; top:-190px; right:-120px; width:480px; height:480px; border:1px solid rgba(255,255,255,.12); border-radius:48%; box-shadow:0 0 0 28px rgba(255,255,255,.025),0 0 0 58px rgba(255,255,255,.018),0 0 0 90px rgba(255,255,255,.012); }
  .dc-welcome-kicker { display:block; margin:0 0 10px; color:var(--dc2-gold); font-size:12px; font-weight:850; letter-spacing:.14em; text-transform:uppercase; }
  .dc-welcome-copy h1 { margin:0 0 10px; color:#fff; font-size:30px; font-weight:780; line-height:1.2; letter-spacing:-.02em; }
  .dc-welcome-copy p { margin:0; color:rgba(255,255,255,.88); font-size:16px; }
  .dc-summary-grid { display:grid; grid-template-columns:repeat(3,1fr); align-items:center; }
  .dc-summary-stat { position:relative; display:flex; min-height:112px; align-items:center; flex-direction:column; justify-content:center; padding:0 24px; color:#fff!important; text-align:center; text-decoration:none!important; }
  a.dc-summary-stat:hover { background:rgba(255,255,255,.06); }
  .dc-summary-stat + .dc-summary-stat { border-left:1px solid rgba(255,255,255,.28); }
  .dc-summary-icon { display:grid; width:50px; height:50px; place-items:center; margin-bottom:8px; color:#fff; background:rgba(1,27,64,.58); border-radius:50%; }
  .dc-summary-icon svg { width:28px; height:28px; }
  .dc-summary-stat strong { color:#fff; font-size:29px; line-height:1.1; }
  .dc-summary-stat > span:last-child { margin-top:5px; color:rgba(255,255,255,.9); font-size:14px; }

  .course-list-heading-container { order:-1; margin-bottom:16px; padding:0; }
  .course-list-title { color:transparent!important; font-size:0!important; }
  .course-list-title::before { content:'My Learning'; color:var(--dc2-text); font-size:28px; font-weight:780; letter-spacing:-.02em; }
  .course-list-title::after { display:none!important; }
  .course-filter-controls-container { align-items:center; }
  .course-filter-controls-container .btn { min-height:44px; border-color:#bdc8d6; border-radius:7px; font-weight:700; }
  .course-filter-controls-container .btn:not(.active) { background:#fff; }
  .course-filter-controls-container input { min-height:44px; border-color:#bdc8d6; border-radius:7px; }
  [role='tablist'] { border-bottom:1px solid var(--dc2-line); }
  [role='tab'] { position:relative; min-height:52px; color:#53627a; font-weight:700; }
  [role='tab'][aria-selected='true'] { color:var(--dc2-blue); }
  [role='tab'][aria-selected='true']::after { content:''; position:absolute; right:0; bottom:-1px; left:0; height:3px; background:var(--dc2-blue); }

  .dc-dashboard-content { display:grid; width:100%; max-width:1540px; grid-template-columns:minmax(0,1fr) 320px; gap:24px; align-items:start; }
  .dc-course-grid { display:grid; min-width:0; grid-template-columns:repeat(2,minmax(0,1fr)); gap:20px; align-items:stretch; }
  .dc-learning-card { display:grid; grid-template-columns:minmax(220px,42%) minmax(0,1fr); min-height:184px; overflow:hidden; background:#fff; border:1px solid #dfe4eb; border-radius:12px; box-shadow:0 7px 20px rgba(9,38,79,.08); transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease; }
  .dc-learning-card:hover { transform:translateY(-2px); border-color:#cbd8e8; box-shadow:0 14px 30px rgba(23,58,94,.13); }
  .dc-card-visual { position:relative; min-height:184px; overflow:hidden; display:flex; align-items:center; justify-content:center; background:linear-gradient(145deg,#06284f,#003f92); }
  .dc-card-visual::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg,rgba(1,25,53,.08),rgba(1,31,70,.24)); pointer-events:none; }
  .dc-card-visual img { width:100%; height:auto; max-height:100%; aspect-ratio:16/9; object-fit:contain; }
  .dc-card-visual > span { position:absolute; z-index:2; top:17px; left:18px; color:var(--dc2-gold); font-size:11px; font-weight:850; letter-spacing:.08em; text-transform:uppercase; }
  .dc-card-content { display:flex; min-width:0; flex-direction:column; padding:18px 20px 15px; }
  .dc-card-title-row { display:flex; align-items:flex-start; gap:12px; }
  .dc-card-title-row h3 { flex:1; min-width:0; margin:0; font-size:17px; font-weight:780; line-height:1.28; }
  .dc-card-title-row h3 a { color:var(--dc2-text); text-decoration:none; }
  .dc-card-more { flex:0 0 auto; color:#183b6a; font-size:16px; font-weight:900; letter-spacing:2px; }
  .dc-card-meta { margin:9px 0 16px; color:var(--dc2-muted); font-size:13px; }
  .dc-card-meta span { padding:0 5px; }
  .dc-progress-row { display:flex; align-items:center; gap:11px; }
  .dc-progress-track { display:block; height:7px; flex:1; overflow:hidden; background:#e5e9ef; border-radius:999px; }
  .dc-progress-track span { display:block; height:100%; background:linear-gradient(90deg,var(--dc2-blue),var(--dc2-blue-bright)); border-radius:inherit; transition:width .35s ease; }
  .dc-progress-row strong { flex:0 0 auto; color:#53627a; font-size:12px; }
  .dc-progress-label { margin:7px 0 0; color:#6d7b91; font-size:12px; }
  .dc-card-footer { display:flex; align-items:center; gap:12px; margin-top:auto; padding-top:13px; border-top:1px solid #e4e8ee; }
  .dc-next-up { display:flex; min-width:0; flex:1; align-items:center; gap:8px; color:#66748b; font-size:12px; }
  .dc-next-up svg { width:17px; height:17px; flex:0 0 auto; }
  .dc-next-up > span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .dc-course-action { display:inline-flex; min-height:34px; align-items:center; justify-content:center; flex:0 0 auto; padding:0 15px; color:#fff!important; background:var(--dc2-blue); border-radius:6px; box-shadow:0 5px 12px rgba(14,92,230,.2); font-size:12px; font-weight:750; text-decoration:none!important; }
  .dc-course-action:hover { background:#084cca; }
  .dc-pagination { grid-column:1/-1; display:flex; justify-content:center; padding:15px 0; }
  #course-list-active-filters-container { grid-column:1/-1; padding:10px 14px; background:#fff; border:1px solid var(--dc2-line); border-radius:8px; }

  .course-list-sidebar, [data-testid='course-list-sidebar'] { display:none!important; }
  .dc-challenge-card { position:sticky; top:24px; display:flex; min-height:388px; align-items:center; flex-direction:column; justify-content:center; padding:30px 26px; text-align:center; background:#fff; border:1px solid #dfe4eb; border-radius:12px; box-shadow:0 7px 20px rgba(9,38,79,.07); }
  .dc-challenge-illustration { display:block; width:min(100%,230px); height:auto; margin:0 auto 18px; }
  .dc-challenge-card h2 { margin:0 0 10px; color:var(--dc2-text); font-size:19px; font-weight:800; line-height:1.3; }
  .dc-challenge-card p { max-width:240px; margin:0 auto 25px; color:var(--dc2-muted); font-size:14px; line-height:1.55; }
  .dc-challenge-card > a { display:inline-flex; width:100%; min-height:46px; align-items:center; justify-content:center; gap:12px; color:#fff!important; background:var(--dc2-blue); border-radius:6px; font-weight:780; text-decoration:none!important; }
  .dc-challenge-card > a:hover { background:#084cca; }

  .dc-certificates-intro { display:flex; align-items:center; justify-content:space-between; gap:28px; margin:0 0 22px; padding:24px 28px; background:#eef5ff; border:1px solid #cfdef0; border-radius:11px; }
  .dc-certificates-kicker { color:var(--dc2-blue); font-size:11px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .dc-certificates-intro h2 { margin:5px 0 4px; color:var(--dc2-text); font-size:24px; }
  .dc-certificates-intro p { margin:0; color:var(--dc2-muted); }
  .dc-certificates-intro > a, .dc-certificate-empty > a { display:inline-flex; min-height:42px; align-items:center; justify-content:center; padding:0 17px; color:#fff!important; background:var(--dc2-blue); border-radius:7px; font-weight:750; text-decoration:none!important; }
  .dc-certificate-loading { min-height:220px; background:linear-gradient(90deg,#f3f6fa 25%,#e8eef5 50%,#f3f6fa 75%); background-size:200% 100%; border-radius:11px; animation:dc-shimmer 1.2s infinite; }
  .dc-certificate-empty { grid-column:1/-1; display:flex; min-height:280px; align-items:center; flex-direction:column; justify-content:center; padding:35px; text-align:center; background:#fff; border:1px dashed #b8c8da; border-radius:12px; }
  .dc-certificate-empty > svg { width:54px; height:54px; color:var(--dc2-blue); }
  .dc-certificate-empty h3 { margin:14px 0 4px; color:var(--dc2-text); }
  .dc-certificate-empty p { margin:0 0 20px; color:var(--dc2-muted); }
  .dc-certificate-list { grid-column:1/-1; display:grid; gap:18px; }
  .dc-certificate-card { position:relative; overflow:hidden; display:grid; grid-template-columns:86px minmax(0,1fr) auto; align-items:center; gap:22px; min-height:150px; padding:24px 26px; background:#fff; border:1px solid #cfdaea; border-radius:12px; box-shadow:0 9px 24px rgba(9,38,79,.08); }
  .dc-certificate-card::after { content:''; position:absolute; right:-55px; width:180px; height:180px; border:1px solid rgba(14,92,230,.12); border-radius:50%; box-shadow:0 0 0 25px rgba(14,92,230,.035); pointer-events:none; }
  .dc-certificate-emblem { position:relative; z-index:1; display:flex; width:72px; height:72px; align-items:center; justify-content:center; color:#fff; background:linear-gradient(145deg,#082f68,#0e5ce6); border-radius:50%; box-shadow:inset 0 0 0 5px rgba(255,255,255,.13); }
  .dc-certificate-emblem svg { width:38px; height:38px; }
  .dc-certificate-copy { position:relative; z-index:1; min-width:0; }
  .dc-certificate-status { color:#0b63d7; font-size:11px; font-weight:850; letter-spacing:.1em; text-transform:uppercase; }
  .dc-certificate-copy h3 { margin:5px 0 7px; color:var(--dc2-text); font-size:20px; line-height:1.3; }
  .dc-certificate-copy p { margin:0 0 5px; color:var(--dc2-muted); font-size:13px; }
  .dc-certificate-course-id { color:#78869a; font-size:11px; }
  .dc-certificate-view { position:relative; z-index:2; display:inline-flex; min-height:43px; align-items:center; justify-content:center; gap:9px; padding:0 17px; color:#fff!important; background:var(--dc2-blue); border-radius:7px; box-shadow:0 5px 13px rgba(14,92,230,.2); font-weight:780; text-decoration:none!important; }
  .dc-certificate-view:hover { background:#084cca; }
  @keyframes dc-shimmer { to { background-position:-200% 0; } }

  .dc-logo-footer { margin-top:64px; color:#fff; background:#041f4e; border-top:3px solid var(--dc2-gold); }
  .dc-footer-main { display:grid; width:min(100% - 64px,1480px); grid-template-columns:minmax(300px,1.45fr) repeat(3,minmax(145px,.55fr)); gap:52px; margin:0 auto; padding:48px 0 38px; }
  .dc-footer-logo { display:inline-flex; padding:8px 12px; background:#fff; border-radius:8px; }
  .dc-footer-logo img { display:block; width:auto; max-width:170px; max-height:46px; object-fit:contain; }
  .dc-footer-brand-block > p { max-width:390px; margin:18px 0 14px; color:#bdcce0; font-size:14px; line-height:1.65; }
  .dc-footer-social { display:flex; flex-wrap:wrap; gap:14px; }
  .dc-footer-social a, .dc-footer-column a { color:#c8d6e7!important; font-size:13px; text-decoration:none!important; }
  .dc-footer-social a:hover, .dc-footer-column a:hover { color:var(--dc2-gold)!important; }
  .dc-footer-column { display:flex; align-items:flex-start; flex-direction:column; gap:10px; }
  .dc-footer-column h2 { margin:3px 0 8px; color:var(--dc2-gold); font-size:13px; letter-spacing:.08em; text-transform:uppercase; }
  .dc-footer-column p { margin:0 0 8px; color:#bdcce0; font-size:13px; line-height:1.55; }
  .dc-footer-column a.dc-footer-demo { display:inline-flex; min-height:40px; align-items:center; padding:0 15px; color:#061f4e!important; background:#ffbd2e!important; border-radius:6px; box-shadow:none!important; font-weight:850; opacity:1; text-shadow:none!important; }
  .dc-footer-bottom { display:flex; width:min(100% - 64px,1480px); min-height:64px; align-items:center; justify-content:space-between; gap:20px; margin:0 auto; color:#aebfd5; border-top:1px solid rgba(255,255,255,.12); font-size:12px; }
  .dc-footer-bottom a { color:#c8d6e7!important; text-decoration:none!important; }
  .dc-logo-footer-minimal { display:flex; min-height:112px; align-items:center; justify-content:center; margin-top:64px; background:linear-gradient(115deg,#031d42 0%,#062957 68%,#0b49a6 100%); border-top:3px solid var(--dc2-gold); }
  .dc-logo-footer-minimal a { display:inline-flex; align-items:center; justify-content:center; padding:8px 12px; background:#fff; border-radius:8px; box-shadow:0 10px 24px rgba(0,0,0,.16); }
  .dc-logo-footer-minimal img { display:block; width:auto; max-width:148px; max-height:42px; object-fit:contain; }

  @media (max-width:1199.98px) {
    .dc-dashboard-hero { grid-template-columns:1fr; gap:22px; }
    .dc-dashboard-content { grid-template-columns:1fr; }
    .dc-course-grid { grid-template-columns:1fr; }
    .dc-challenge-card { position:relative; top:auto; min-height:330px; }
    .dc-footer-main { grid-template-columns:1.2fr repeat(2,1fr); }
    .dc-footer-cta { grid-column:2; }
  }
  @media (max-width:768.98px) {
    .dc-site-header-desktop { display:none; }
    .dc-site-header-mobile { display:flex; }
    main .container-xl { padding-right:16px; padding-left:16px; }
    .dc-dashboard-hero { min-height:0; margin:14px 0 22px; padding:26px 22px; border-radius:10px; }
    .dc-dashboard-content { width:100%; }
    .dc-summary-grid { grid-template-columns:repeat(3,1fr); }
    .dc-summary-stat { min-height:92px; padding:0 8px; }
    .dc-summary-icon { width:42px; height:42px; }
    .dc-summary-icon svg { width:23px; height:23px; }
    .dc-summary-stat strong { font-size:23px; }
    .dc-summary-stat > span:last-child { font-size:11px; }
    .dc-learning-card { grid-template-columns:112px minmax(0,1fr); min-height:230px; }
    .dc-card-visual { min-height:230px; }
    .dc-card-content { padding:15px; }
    .dc-card-footer { align-items:stretch; flex-direction:column; }
    .dc-course-action { width:100%; min-height:40px; }
    .dc-certificates-intro { align-items:flex-start; flex-direction:column; }
    .dc-certificate-card { grid-template-columns:64px minmax(0,1fr); }
    .dc-certificate-emblem { width:58px; height:58px; }
    .dc-certificate-view { grid-column:1/-1; width:100%; }
    .dc-footer-main { width:calc(100% - 36px); grid-template-columns:1fr; gap:28px; }
    .dc-footer-cta { grid-column:auto; }
    .dc-footer-bottom { width:calc(100% - 36px); align-items:flex-start; flex-direction:column; justify-content:center; padding:18px 0; }
  }
  @media (max-width:480px) {
    .dc-summary-grid { grid-template-columns:1fr; }
    .dc-summary-stat { min-height:80px; flex-direction:row; gap:12px; justify-content:flex-start; text-align:left; }
    .dc-summary-stat + .dc-summary-stat { border-top:1px solid rgba(255,255,255,.22); border-left:0; }
    .dc-summary-stat > span:last-child { margin:0; }
    .dc-learning-card { grid-template-columns:1fr; }
    .dc-card-visual { min-height:150px; max-height:170px; }
  }
  @media (prefers-reduced-motion:reduce) { .dc-progress-track span { transition:none; } }
`;

const DaracorpDashboardV2 = ({ courseListData }) => {
  const courses = useDcAllCourseData();
  const certificateState = useDcCertificates();
  const summary = useDcDashboardSummary(courses, certificateState.records);
  const certificateView = new URLSearchParams(window.location.search).get('view') === 'certificates';
  const [portalTarget, setPortalTarget] = DCReact.useState(null);
  DCReact.useEffect(() => {
    let active = true;
    const locate = () => {
      const target = document.querySelector('#dashboard-content');
      if (active && target) setPortalTarget(target);
    };
    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { active = false; observer.disconnect(); };
  }, []);
  DCReact.useEffect(() => {
    let active = true;
    const hideAdminProxySearch = () => {
      if (!active) return;
      document.querySelectorAll('input[placeholder="Username or email"]').forEach(input => {
        const form = input.closest('form');
        if (!form) return;
        let target = form;
        let current = form.parentElement;
        while (current && current !== document.body && !current.matches('main,#main')) {
          const rect = current.getBoundingClientRect();
          if (rect.width >= window.innerWidth * .65 && rect.height > 20 && rect.height < 150) target = current;
          current = current.parentElement;
        }
        target.hidden = true;
        target.style.setProperty('display','none','important');
      });
    };
    hideAdminProxySearch();
    const observer = new MutationObserver(hideAdminProxySearch);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { active = false; observer.disconnect(); };
  }, []);
  return (
    <>
      <style data-daracorp-dashboard-v7={DC_DASHBOARD_BUILD}>{DARACORP_DASHBOARD_V2_CSS}</style>
      {portalTarget && dcCreatePortal(<DaracorpWelcome summary={summary} />, portalTarget)}
      {!certificateView && courseListData.showFilters && <div id="course-list-active-filters-container"><DCActiveCourseFilters {...courseListData.filterOptions} /></div>}
      {certificateView && <DaracorpCertificatesIntro count={summary.certificates} />}
      <div className="dc-dashboard-content">
        <div className="dc-course-grid">
          {!certificateView && courseListData.visibleList.map(({ cardId }) => <DaracorpCourseCard key={cardId} cardId={cardId} />)}
          {certificateView && certificateState.loaded && certificateState.records.length > 0 && (
            <div className="dc-certificate-list">
              {certificateState.records.map((record, index) => <DaracorpCertificateCard key={record?.download_url || record?.course_id || index} record={record} />)}
            </div>
          )}
          {certificateView && !certificateState.loaded && <div className="dc-certificate-loading" aria-label="Loading completed certificates" />}
          {certificateView && certificateState.loaded && certificateState.records.length === 0 && (
            <div className="dc-certificate-empty"><DCAwardIcon /><h3>No completed certificates yet</h3><p>Your completed, certificate-eligible courses will appear here.</p><a href="/learner-dashboard/">Continue learning</a></div>
          )}
          {!certificateView && courseListData.numPages > 1 && (
            <div className="dc-pagination">
              <DCPagination variant="secondary" paginationLabel="Course list" pageCount={courseListData.numPages} onPageSelect={courseListData.setPageNumber} />
            </div>
          )}
        </div>
        {!certificateView && <DaracorpChallenge />}
      </div>
    </>
  );
};

// DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3_2026
{
  const { getAuthenticatedHttpClient: dcFeedbackHttpClient } = await import('@edx/frontend-platform/auth');
  const { getConfig: dcFeedbackGetConfig } = await import('@edx/frontend-platform');

  (() => {
    if (window.__DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3__) return;
    window.__DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3__ = true;

    const BUILD = 'DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3_2026';
    const API_PATH = '/api/daracorp/v1/course-feedback/';
    const style = document.createElement('style');
    style.id = 'daracorp-dashboard-alignment-feedback-v1-style';
    style.dataset.build = BUILD;
    style.textContent = `
      :root { --dc-feedback-shell:1420px; --dc-feedback-gutter:40px; --dc-feedback-navy:#072d65; --dc-feedback-blue:#0d5ee8; --dc-feedback-gold:#ffb81c; --dc-feedback-ink:#062653; --dc-feedback-muted:#60718c; --dc-feedback-line:#dbe3ee; --dc-feedback-pale:#eaf3ff; }
      .dc-header-inner,
      #dashboard-content > .dc-dashboard-hero,
      #dashboard-content > .container-mw-xl {
        width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell))!important;
        max-width:var(--dc-feedback-shell)!important;
        margin-right:auto!important;
        margin-left:auto!important;
      }
      #dashboard-content > .container-mw-xl { padding-right:0!important; padding-left:0!important; }
      #dashboard-content .course-list-column,
      #dashboard-content .course-list-container,
      #dashboard-content .course-list-heading-container,
      #dashboard-content .dc-dashboard-content { width:100%!important; max-width:none!important; margin-right:0!important; margin-left:0!important; }
      #dashboard-content .course-list-heading-container { padding-right:0!important; padding-left:0!important; }
      .dc-site-header .dc-header-logo { margin-left:0!important; }
      .dc-site-header .dc-utility-nav { margin-right:0!important; }
      .dc-feedback-completion-v1 { display:flex; width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell)); align-items:center; gap:14px; margin:0 auto 23px; padding:15px 18px; color:var(--dc-feedback-ink); background:var(--dc-feedback-pale); border:1px solid #bfd4ef; border-radius:10px; }
      .dc-feedback-completion-icon-v1 { display:grid; width:38px; height:38px; place-items:center; flex:0 0 auto; color:#fff; background:var(--dc-feedback-blue); border-radius:50%; font-size:20px; }
      .dc-feedback-completion-copy-v1 { min-width:0; }
      .dc-feedback-completion-copy-v1 strong { display:block; margin-bottom:2px; font-weight:760; }
      .dc-feedback-completion-copy-v1 span { color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-open-v1 { min-height:40px; margin-left:auto; padding:0 16px; color:#fff; background:var(--dc-feedback-blue); border:0; border-radius:7px; font-weight:760; cursor:pointer; }
      .dc-feedback-open-v1:hover { background:#084fca; }
      .dc-feedback-card-action-v1 { min-height:34px; padding:0 13px; color:var(--dc-feedback-blue); background:#fff; border:1px solid #9eb9dc; border-radius:6px; font-size:12px; font-weight:750; cursor:pointer; }
      .dc-feedback-card-action-v1:hover { background:#eef5ff; }
      .dc-feedback-course-cta-v1 { display:flex; width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell)); align-items:center; justify-content:space-between; gap:18px; margin:34px auto; padding:22px 24px; color:var(--dc-feedback-ink); background:#fff; border:1px solid var(--dc-feedback-line); border-radius:12px; box-shadow:0 8px 24px rgba(7,45,101,.08); }
      .dc-feedback-course-cta-v1 h2 { margin:0 0 5px; color:var(--dc-feedback-ink); font-size:20px; }
      .dc-feedback-course-cta-v1 p { margin:0; color:var(--dc-feedback-muted); }
      .dc-feedback-modal-backdrop-v1 { position:fixed; inset:0; z-index:2147483500; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(2,25,60,.62); backdrop-filter:blur(3px); }
      .dc-feedback-modal-v1 { width:min(100%,540px); overflow:hidden; color:var(--dc-feedback-ink); background:#fff; border:1px solid var(--dc-feedback-line); border-radius:14px; box-shadow:0 28px 80px rgba(0,25,64,.34); }
      .dc-feedback-modal-v1::before { content:''; display:block; height:6px; background:linear-gradient(90deg,var(--dc-feedback-gold) 0 34%,var(--dc-feedback-blue) 34% 100%); }
      .dc-feedback-modal-head-v1 { display:flex; align-items:flex-start; justify-content:space-between; gap:18px; padding:24px 25px 10px; }
      .dc-feedback-modal-head-v1 h2 { margin:0 0 5px; color:var(--dc-feedback-ink); font-size:24px; }
      .dc-feedback-modal-head-v1 p { margin:0; color:var(--dc-feedback-muted); font-size:14px; line-height:1.5; }
      .dc-feedback-close-v1 { display:grid; width:34px; height:34px; place-items:center; flex:0 0 auto; color:#52647c; background:#f2f5f9; border:0; border-radius:50%; font-size:21px; cursor:pointer; }
      .dc-feedback-form-v1 { padding:12px 25px 25px; }
      .dc-feedback-label-v1 { display:block; margin:0 0 8px; font-size:13px; font-weight:750; }
      .dc-feedback-stars-v1 { display:flex; gap:8px; margin-bottom:20px; }
      .dc-feedback-star-v1 { display:grid; width:44px; height:44px; place-items:center; color:#9aa8ba; background:#f6f8fb; border:1px solid #d6deea; border-radius:9px; font-size:25px; cursor:pointer; }
      .dc-feedback-star-v1.is-selected { color:#fff; background:var(--dc-feedback-gold); border-color:var(--dc-feedback-gold); }
      .dc-feedback-form-v1 textarea { width:100%; min-height:120px; resize:vertical; padding:12px 13px; color:var(--dc-feedback-ink); background:#fff; border:1px solid #bdc9d9; border-radius:8px; font:inherit; line-height:1.5; }
      .dc-feedback-check-v1 { display:flex; align-items:center; gap:8px; margin:13px 0 18px; color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-form-actions-v1 { display:flex; align-items:center; justify-content:flex-end; gap:10px; }
      .dc-feedback-cancel-v1,.dc-feedback-submit-v1 { min-height:42px; padding:0 17px; border-radius:7px; font-weight:750; cursor:pointer; }
      .dc-feedback-cancel-v1 { color:var(--dc-feedback-ink); background:#fff; border:1px solid #bdc9d9; }
      .dc-feedback-submit-v1 { color:#fff; background:var(--dc-feedback-blue); border:1px solid var(--dc-feedback-blue); }
      .dc-feedback-submit-v1:disabled { cursor:not-allowed; opacity:.55; }
      .dc-feedback-status-v1 { min-height:20px; margin:0 auto 0 0; color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-submit-v1.is-saving { position:relative; padding-left:42px; }
      .dc-feedback-submit-v1.is-saving::before { content:''; position:absolute; left:17px; width:14px; height:14px; border:2px solid rgba(255,255,255,.45); border-top-color:#fff; border-radius:50%; animation:dc-feedback-spin-v2 .7s linear infinite; }
      .dc-feedback-success-v2 { display:grid; min-height:390px; place-items:center; padding:42px 32px 36px; text-align:center; }
      .dc-feedback-success-icon-v2 { display:grid; width:84px; height:84px; place-items:center; margin:0 auto 22px; color:#fff; background:linear-gradient(145deg,#0b61e7,#1b7cff); border:8px solid #eaf3ff; border-radius:50%; box-shadow:0 14px 34px rgba(13,94,232,.24); font-size:40px; font-weight:850; }
      .dc-feedback-success-v2 h2 { margin:0 0 10px; color:var(--dc-feedback-ink); font-size:28px; }
      .dc-feedback-success-v2 p { max-width:360px; margin:0 auto 25px; color:var(--dc-feedback-muted); line-height:1.55; }
      .dc-feedback-done-v2 { min-width:150px; min-height:44px; color:#fff; background:var(--dc-feedback-blue); border:0; border-radius:8px; font-weight:780; cursor:pointer; }
      @keyframes dc-feedback-spin-v2 { to { transform:rotate(360deg); } }
      @media (max-width:768px) { :root { --dc-feedback-gutter:28px; } .dc-feedback-completion-v1,.dc-feedback-course-cta-v1 { align-items:flex-start; flex-direction:column; } .dc-feedback-open-v1 { width:100%; margin-left:0; } }
    `;
    document.head.appendChild(style);

    const apiUrl = () => `${String(dcFeedbackGetConfig().LMS_BASE_URL || '').replace(/\/$/,'')}${API_PATH}`;
    const absoluteCourseId = value => {
      const decoded = decodeURIComponent(String(value || ''));
      const match = decoded.match(/course-v1:[^/?#]+/);
      return match ? match[0] : '';
    };
    const courseFromCard = card => {
      const link = card.querySelector('.dc-card-title-row h3 a, h3 a, a[href*="course-v1:"]');
      return { courseId:absoluteCourseId(link?.href), courseName:(link?.textContent || 'this course').trim() };
    };
    const isCompleteCard = card => {
      const text = (card.textContent || '').replace(/\s+/g,' ');
      const fill = card.querySelector('.dc-progress-track > span');
      const width = parseFloat(fill?.style?.width || '0');
      return /100%\s*complete/i.test(text) || width >= 100;
    };

    const closeModal = () => {
      document.querySelector('.dc-feedback-modal-backdrop-v1')?.remove();
      document.body.style.removeProperty('overflow');
    };

    const openModal = async (courseId, courseName) => {
      closeModal();
      let rating = 0;
      const backdrop = document.createElement('div');
      backdrop.className = 'dc-feedback-modal-backdrop-v1';
      backdrop.innerHTML = `
        <section class="dc-feedback-modal-v1" role="dialog" aria-modal="true" aria-labelledby="dc-feedback-title-v1">
          <div class="dc-feedback-modal-head-v1"><div><h2 id="dc-feedback-title-v1">How was your course?</h2><p class="dc-feedback-course-name-v1"></p></div><button type="button" class="dc-feedback-close-v1" aria-label="Close">×</button></div>
          <form class="dc-feedback-form-v1">
            <span class="dc-feedback-label-v1">Overall experience</span>
            <div class="dc-feedback-stars-v1" role="radiogroup" aria-label="Course rating"></div>
            <label class="dc-feedback-label-v1" for="dc-feedback-comment-v1">What worked well, or what should we improve? <span style="font-weight:400">(optional)</span></label>
            <textarea id="dc-feedback-comment-v1" maxlength="2000" placeholder="Share a short comment"></textarea>
            <label class="dc-feedback-check-v1"><input type="checkbox" name="anonymous"> Submit my comment anonymously</label>
            <div class="dc-feedback-form-actions-v1"><span class="dc-feedback-status-v1" aria-live="polite"></span><button type="button" class="dc-feedback-cancel-v1">Cancel</button><button type="submit" class="dc-feedback-submit-v1" disabled>Submit feedback</button></div>
          </form>
        </section>`;
      backdrop.querySelector('.dc-feedback-course-name-v1').textContent = courseName || 'Course feedback';
      const stars = backdrop.querySelector('.dc-feedback-stars-v1');
      const submit = backdrop.querySelector('.dc-feedback-submit-v1');
      const renderStars = () => stars.querySelectorAll('button').forEach((button,index) => {
        const selected = index < rating;
        button.classList.toggle('is-selected',selected);
        button.setAttribute('aria-checked',String(index + 1 === rating));
      });
      for (let value=1; value<=5; value += 1) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'dc-feedback-star-v1'; button.textContent = '★';
        button.setAttribute('role','radio'); button.setAttribute('aria-label',`${value} star${value === 1 ? '' : 's'}`);
        button.addEventListener('click',() => { rating=value; renderStars(); submit.disabled=false; });
        stars.appendChild(button);
      }
      renderStars();
      backdrop.addEventListener('click',event => { if (event.target === backdrop) closeModal(); });
      backdrop.querySelector('.dc-feedback-close-v1').addEventListener('click',closeModal);
      backdrop.querySelector('.dc-feedback-cancel-v1').addEventListener('click',closeModal);
      document.addEventListener('keydown',function escapeFeedback(event){ if(event.key === 'Escape'){ document.removeEventListener('keydown',escapeFeedback); closeModal(); } });
      document.body.appendChild(backdrop); document.body.style.overflow='hidden';
      backdrop.querySelector('.dc-feedback-close-v1').focus();

      if (courseId) {
        try {
          const response = await dcFeedbackHttpClient().get(`${apiUrl()}?course_id=${encodeURIComponent(courseId)}`);
          const existing = response?.data?.feedback;
          if (existing) {
            rating = Number(existing.rating || 0);
            backdrop.querySelector('textarea').value = existing.comment || '';
            backdrop.querySelector('[name="anonymous"]').checked = Boolean(existing.anonymous);
            submit.disabled = rating < 1; renderStars();
          }
        } catch (error) { /* New feedback has no existing record. */ }
      }

      backdrop.querySelector('form').addEventListener('submit',async event => {
        event.preventDefault();
        const status = backdrop.querySelector('.dc-feedback-status-v1');
        if (rating < 1) { status.textContent='Choose a star rating before submitting.'; return; }
        if (!courseId) { status.textContent='We could not identify this course. Refresh the page and try again.'; return; }
        submit.disabled=true;
        submit.classList.add('is-saving');
        submit.textContent='Submitting…';
        status.textContent='Saving securely…';
        backdrop.querySelector('form').setAttribute('aria-busy','true');
        try {
          const response = await dcFeedbackHttpClient().post(apiUrl(),{
            course_id:courseId,
            course_name:courseName || '',
            rating,
            comment:backdrop.querySelector('textarea').value.trim(),
            anonymous:backdrop.querySelector('[name="anonymous"]').checked,
          });
          if (response?.data?.saved !== true) throw new Error('Feedback service did not confirm the save.');
          localStorage.setItem(`daracorp-feedback:${courseId}`,'submitted');
          const modal = backdrop.querySelector('.dc-feedback-modal-v1');
          modal.innerHTML = `<div class="dc-feedback-success-v2" role="status"><div><span class="dc-feedback-success-icon-v2" aria-hidden="true">✓</span><h2>Feedback submitted</h2><p>Thank you. Your response has been saved and will help us improve this course.</p><button type="button" class="dc-feedback-done-v2">Done</button></div></div>`;
          modal.querySelector('.dc-feedback-done-v2').addEventListener('click',closeModal);
          modal.querySelector('.dc-feedback-done-v2').focus();
          document.querySelectorAll('.dc-feedback-card-action-v1,.dc-feedback-open-v1').forEach(button => {
            if ((button.textContent || '').match(/feedback/i)) button.textContent='Feedback submitted ✓';
          });
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message || '';
          console.error('Daracorp feedback submission failed', error);
          status.textContent=detail ? `Not submitted: ${detail}` : 'We could not save your feedback. Please try again.';
          submit.disabled=false;
          submit.classList.remove('is-saving');
          submit.textContent='Submit feedback';
          backdrop.querySelector('form').removeAttribute('aria-busy');
        }
      });
    };

    const enhanceDashboard = () => {
      if (!/\/learner-dashboard\/?/.test(window.location.pathname)) return;
      const cards = Array.from(document.querySelectorAll('.dc-learning-card'));
      const completed = cards.filter(isCompleteCard);
      completed.forEach(card => {
        if (card.querySelector('.dc-feedback-card-action-v1')) return;
        const info = courseFromCard(card); if (!info.courseId) return;
        const footer = card.querySelector('.dc-card-footer'); if (!footer) return;
        const button = document.createElement('button'); button.type='button'; button.className='dc-feedback-card-action-v1'; button.textContent='Give feedback';
        button.addEventListener('click',() => openModal(info.courseId,info.courseName)); footer.appendChild(button);
      });
      if (!completed.length && new URLSearchParams(window.location.search).get('previewFeedback') !== '1') return;
      if (document.querySelector('.dc-feedback-completion-v1')) return;
      const info = completed.length ? courseFromCard(completed[0]) : courseFromCard(cards[0]);
      if (!info.courseId) return;
      const banner = document.createElement('section'); banner.className='dc-feedback-completion-v1';
      banner.innerHTML='<span class="dc-feedback-completion-icon-v1" aria-hidden="true">✓</span><div class="dc-feedback-completion-copy-v1"><strong>Course completed — congratulations.</strong><span>Tell us what helped and what we can improve.</span></div><button type="button" class="dc-feedback-open-v1">Share course feedback</button>';
      banner.querySelector('button').addEventListener('click',() => openModal(info.courseId,info.courseName));
      const hero = document.querySelector('#dashboard-content > .dc-dashboard-hero');
      if (hero) hero.insertAdjacentElement('afterend',banner);
    };

    const enhanceLearning = () => {
      const match = decodeURIComponent(window.location.pathname).match(/\/learning\/course\/(course-v1:[^/]+)\/home\/?$/);
      if (!match || document.querySelector('.dc-feedback-course-cta-v1')) return;
      const main = document.querySelector('main,#main'); if (!main) return;
      const courseId = match[1];
      const title = (document.querySelector('header h1,header h2,main h1')?.textContent || 'this course').trim();
      const card = document.createElement('section'); card.className='dc-feedback-course-cta-v1';
      card.innerHTML='<div><h2>Finished this course?</h2><p>Your feedback helps us strengthen the learning experience.</p></div><button type="button" class="dc-feedback-open-v1">Share course feedback</button>';
      card.querySelector('button').addEventListener('click',() => openModal(courseId,title)); main.appendChild(card);
    };

    const enhance = () => { enhanceDashboard(); enhanceLearning(); };
    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(document.documentElement,{childList:true,subtree:true});
  })();
}
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_dashboard_v7_desktop_header',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpDesktopHeader,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.layout.header_mobile.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_dashboard_v7_mobile_header',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpMobileHeader,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.learner_dashboard.course_list.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.learner_dashboard.course_list.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_dashboard_v7_course_grid',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpDashboardV2,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_dashboard_v7_brand_footer',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpMinimalLogoFooter,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'learning') {
const dcCourseReactModule = await import('react');
const DCCourseReact = dcCourseReactModule.default;
const { createPortal: dcCourseCreatePortal } = await import('react-dom');
const {
  getAuthenticatedHttpClient: dcCourseHttpClient,
  getAuthenticatedUser: dcCourseGetUser,
} = await import('@edx/frontend-platform/auth');
const { getConfig: dcCourseGetConfig } = await import('@edx/frontend-platform');
const { useModel: useDCCourseModel } = await import('@src/generic/model-store');
const { useSequenceNavigationMetadata: useDCSequenceNavigationMetadata } = await import('@src/courseware/course/sequence/sequence-navigation/hooks');

const DC_COURSE_LOGO_URL = 'https://learn.daracorp.io/media/afrolms/daracorp_logo.png';
const DC_COURSE_BUILD = 'DARACORP_COURSE_EXPERIENCE_V3_NAVIGATION_FIX_2026';

const dcCourseIdFromLocation = () => {
  const match = window.location.pathname.match(/\/course\/([^/]+)/);
  if (!match) return '';
  try { return decodeURIComponent(match[1]); } catch (error) { return match[1]; }
};

const dcCourseProgressFromData = (data, outlineData = null) => {
  const summary = data?.completion_summary || {};
  const complete = Number(summary.complete_count || 0);
  const incomplete = Number(summary.incomplete_count || 0);
  const locked = Number(summary.locked_count || 0);
  const totalBlocks = complete + incomplete + locked;
  const grade = Number(data?.course_grade?.percent);
  const ratio = totalBlocks > 0
    ? complete / totalBlocks
    : (Number.isFinite(grade) ? grade : 0);
  // section_scores describes graded subsections, not outline completion.  The
  // course-home outline is the authoritative source used by the native UI.
  const blocks = outlineData?.course_blocks?.blocks || {};
  const outlineSections = Object.values(blocks).filter(block => block?.type === 'chapter' && !block?.hide_from_toc);
  const gradedSections = Array.isArray(data?.section_scores) ? data.section_scores : [];
  const completedSections = outlineSections.length
    ? outlineSections.filter(section => section.complete === true).length
    : gradedSections.filter(section => {
        const subsections = Array.isArray(section?.subsections) ? section.subsections : [];
        return subsections.length > 0 && subsections.every(item => Number(item?.percent_graded || 0) >= 1);
      }).length;
  return {
    percent: Math.max(0, Math.min(100, Math.round(ratio * 100))),
    completedSections,
    totalSections: outlineSections.length || gradedSections.length,
  };
};

const useDCCourseProgress = (courseId) => {
  const [progress, setProgress] = DCCourseReact.useState({
    percent: 0,
    completedSections: 0,
    totalSections: 0,
    loading: true,
  });
  DCCourseReact.useEffect(() => {
    let active = true;
    if (!courseId) {
      setProgress(value => ({ ...value, loading: false }));
      return () => { active = false; };
    }
    Promise.all([
      dcCourseHttpClient().get(`${dcCourseGetConfig().LMS_BASE_URL}/api/course_home/progress/${courseId}`),
      dcCourseHttpClient().get(`${dcCourseGetConfig().LMS_BASE_URL}/api/course_home/outline/${courseId}`),
    ])
      .then(([progressResponse, outlineResponse]) => {
        if (active) setProgress({ ...dcCourseProgressFromData(progressResponse.data, outlineResponse.data), loading: false });
      })
      .catch(() => {
        if (active) setProgress(value => ({ ...value, loading: false }));
      });
    return () => { active = false; };
  }, [courseId]);
  return progress;
};

const DCArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
  </svg>
);

const DCArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

const DCChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m7 10 5 5 5-5" />
  </svg>
);

const DaracorpLearningHeader = ({ courseOrg, courseNumber, courseTitle, showUserDropdown }) => {
  let learner = {};
  try { learner = dcCourseGetUser() || {}; } catch (error) { learner = {}; }
  const displayName = learner.name || learner.username || 'Learner';
  const firstName = displayName.trim().split(/\s+/)[0];
  const initial = firstName.charAt(0).toUpperCase();
  const config = dcCourseGetConfig();
  const courseId = dcCourseIdFromLocation();
  const mfeCourseBase = `/learning/course/${courseId}`;
  const lmsCourseBase = `${config.LMS_BASE_URL}/courses/${courseId}`;
  const currentPath = window.location.pathname;
  const courseTabActive = !/\/(progress|dates)(\/|$)/.test(currentPath) && !/\/(refreshment|certificates)(\/|$)/.test(currentPath);
  return (
    <header className="dc-course-header">
      <style data-daracorp-course-experience-v2={DC_COURSE_BUILD}>{DARACORP_COURSE_EXPERIENCE_CSS}</style>
      <a className="sr-only sr-only-focusable" href="#main-content">Skip to main content</a>
      <div className="dc-course-header-inner">
        <a className="dc-course-logo" href="https://apps.learn.daracorp.io/learner-dashboard/" aria-label="Daracorp dashboard">
          <img src={DC_COURSE_LOGO_URL} alt="Daracorp" width="170" height="48" />
        </a>
        <span className="dc-course-header-divider" aria-hidden="true" />
        <div className="dc-course-identity">
          <span>{[courseOrg, courseNumber].filter(Boolean).join(' ')}</span>
          <strong>{courseTitle || 'Course'}</strong>
        </div>
        {showUserDropdown && (
          <a className="dc-course-account" href={config.ACCOUNT_SETTINGS_URL || `${config.LMS_BASE_URL}/account/settings`}>
            <span className="dc-course-avatar">{initial}</span>
            <span>{firstName}</span>
            <DCChevronDown />
          </a>
        )}
      </div>
      {courseId && (
        <nav className="dc-course-quick-nav" aria-label="Course sections">
          <a className={courseTabActive ? 'active' : ''} href={`${mfeCourseBase}/home`}>Course</a>
          <a className={/\/progress(\/|$)/.test(currentPath) ? 'active' : ''} href={`${mfeCourseBase}/progress`}>Progress</a>
          <a className={/\/dates(\/|$)/.test(currentPath) ? 'active' : ''} href={`${mfeCourseBase}/dates`}>Dates</a>
          <a className={/\/refreshment(\/|$)/.test(currentPath) ? 'active' : ''} href={`${lmsCourseBase}/refreshment/`}>Refreshment</a>
          <a className={/\/certificates(\/|$)/.test(currentPath) ? 'active' : ''} href={`${lmsCourseBase}/certificates/`}>Certificates</a>
        </nav>
      )}
    </header>
  );
};

const DaracorpOutlineProgress = () => {
  const courseId = dcCourseIdFromLocation();
  const progress = useDCCourseProgress(courseId);
  const progressAngle = `${progress.percent * 3.6}deg`;
  const ringStyle = { '--dc-course-progress-angle': progressAngle };
  return (
    <section className="dc-outline-progress" aria-label="Course progress">
      <h2>Course progress</h2>
      <div className="dc-outline-progress-body">
        <div className="dc-progress-ring" style={ringStyle}>
          <span><strong>{progress.loading ? '...' : `${progress.percent}%`}</strong><small>Complete</small></span>
        </div>
        <div className="dc-progress-copy">
          {progress.totalSections > 0 ? (
            <strong>{progress.completedSections} of {progress.totalSections} units completed</strong>
          ) : (
            <strong>Keep your learning momentum going</strong>
          )}
          <a href={`/learning/course/${courseId}/progress`}>View progress</a>
        </div>
      </div>
    </section>
  );
};

const DaracorpCourseChrome = () => {
  const [portalTarget, setPortalTarget] = DCCourseReact.useState(null);
  DCCourseReact.useEffect(() => {
    let active = true;
    const locate = () => {
      const target = document.querySelector('.outline-sidebar');
      if (active && target) setPortalTarget(target);
    };
    locate();
    const observer = new MutationObserver(locate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { active = false; observer.disconnect(); };
  }, []);
  return (
    <>
      {portalTarget && dcCourseCreatePortal(<DaracorpOutlineProgress />, portalTarget)}
    </>
  );
};

const DaracorpSequenceNavigation = ({ sequenceId, unitId, nextHandler, previousHandler }) => {
  const sequence = useDCCourseModel('sequences', sequenceId) || {};
  const navigation = useDCSequenceNavigationMetadata(sequenceId, unitId) || {};
  const unitIds = Array.isArray(sequence.unitIds) ? sequence.unitIds : [];
  const foundIndex = unitIds.indexOf(unitId);
  const current = foundIndex >= 0 ? foundIndex + 1 : 1;
  const total = Math.max(unitIds.length, 1);
  const progressWidth = `${Math.round((current / total) * 100)}%`;
  const progressStyle = { width: progressWidth };
  const dcLearningHref = (link) => link ? `/learning${link.startsWith('/') ? '' : '/'}${link}` : '';
  const previousLink = dcLearningHref(navigation.previousLink || '');
  const nextLink = dcLearningHref(navigation.nextLink || '');
  const handleFallback = (handler) => (event) => {
    if (event.currentTarget.getAttribute('href')) return;
    event.preventDefault();
    if (typeof handler === 'function') handler();
  };
  return (
    <nav className="dc-lesson-navigation" aria-label="Lesson navigation">
      <a className={`dc-lesson-nav-button previous${previousLink ? '' : ' is-disabled'}`} href={previousLink || undefined} aria-disabled={!previousLink} onClick={handleFallback(previousHandler)}>
        <DCArrowLeft /><span>Previous section</span>
      </a>
      <div className="dc-lesson-position" aria-label={`Section ${current} of ${total}`}>
        <strong>Section {current} of {total}</strong>
        <span className="dc-lesson-progress"><span style={progressStyle} /></span>
      </div>
      <a className={`dc-lesson-nav-button next${nextLink ? '' : ' is-disabled'}`} href={nextLink || undefined} aria-disabled={!nextLink} onClick={handleFallback(nextHandler)}>
        <span>Next section</span><DCArrowRight />
      </a>
    </nav>
  );
};

const DaracorpCourseFooter = () => (
  <footer className="dc-course-footer" aria-label="Daracorp footer">
    <div className="dc-course-footer-main">
      <div className="dc-course-footer-brand">
        <a className="dc-course-footer-logo" href="https://daracorp.io/" target="_blank" rel="noopener noreferrer"><img src={DC_COURSE_LOGO_URL} alt="Daracorp" /></a>
        <p>Practical compliance learning built for confident decisions, stronger teams, and lasting growth.</p>
        <div className="dc-course-footer-social"><a href="https://www.linkedin.com/company/daracorp" target="_blank" rel="noopener noreferrer">LinkedIn</a><a href="https://www.instagram.com/daracorpethioadvise" target="_blank" rel="noopener noreferrer">Instagram</a><a href="https://x.com/DaraCorpEthioAd" target="_blank" rel="noopener noreferrer">X</a></div>
      </div>
      <nav className="dc-course-footer-column" aria-label="Explore"><h2>Explore</h2><a href="https://learn.daracorp.io/courses">Courses</a><a href="https://learn.daracorp.io/courses?view=learning-paths">Learning paths</a><a href="https://daracorp.io/why" target="_blank" rel="noopener noreferrer">Why Daracorp</a><a href="https://daracorp.io/contact" target="_blank" rel="noopener noreferrer">Contact</a></nav>
      <nav className="dc-course-footer-column" aria-label="Legal"><h2>Legal</h2><a href="https://daracorp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy</a><a href="https://daracorp.io/terms" target="_blank" rel="noopener noreferrer">Terms</a><a href="https://daracorp.io/security" target="_blank" rel="noopener noreferrer">Security</a></nav>
      <div className="dc-course-footer-column"><h2>Get started</h2><p>See Daracorp in action for your team.</p><a className="dc-course-footer-demo" href="https://daracorp.io/demo" target="_blank" rel="noopener noreferrer">Request a demo</a></div>
    </div>
    <div className="dc-course-footer-bottom"><span>© 2026 Daracorp. All rights reserved.</span><a href="https://daracorp.io/" target="_blank" rel="noopener noreferrer">daracorp.io</a></div>
  </footer>
);

const DaracorpMinimalCourseFooter = () => (
  <footer className="dc-course-footer-minimal" aria-label="Daracorp footer">
    <a href="https://daracorp.io/" target="_blank" rel="noopener noreferrer" aria-label="Daracorp home"><img src={DC_COURSE_LOGO_URL} alt="Daracorp" /></a>
  </footer>
);

const DARACORP_COURSE_EXPERIENCE_CSS = `
  :root {
    --dc-course-navy:#062957;
    --dc-course-navy-deep:#031d42;
    --dc-course-blue:#0e5ce6;
    --dc-course-gold:#e3a11a;
    --dc-course-gold-bright:#ffbd2e;
    --dc-course-text:#09264f;
    --dc-course-muted:#64738b;
    --dc-course-line:#dfe6ef;
    --dc-course-canvas:#f4f7fb;
    --dc-course-success:#169b68;
  }

  html, body, #root { background:var(--dc-course-canvas); color:var(--dc-course-text); }
  body { min-width:320px; }

  .dc-course-header { position:relative; z-index:1000; background:#fff; border-bottom:1px solid #e4e9f0; }
  .dc-course-header-inner { display:flex; min-height:76px; align-items:center; width:min(100% - 64px,1480px); margin:0 auto; }
  .dc-course-logo { display:flex; width:176px; align-items:center; flex:0 0 auto; }
  .dc-course-logo img { display:block; width:auto; max-width:170px; max-height:48px; object-fit:contain; }
  .dc-course-header-divider { width:1px; height:40px; margin:0 20px; background:#dfe5ed; }
  .dc-course-identity { display:flex; min-width:0; flex-direction:column; gap:2px; }
  .dc-course-identity span { overflow:hidden; color:#5f6f86; font-size:13px; text-overflow:ellipsis; white-space:nowrap; }
  .dc-course-identity strong { overflow:hidden; color:var(--dc-course-text); font-size:15px; text-overflow:ellipsis; white-space:nowrap; }
  .dc-course-account { display:flex; min-height:44px; align-items:center; gap:10px; margin-left:auto; padding:4px 8px 4px 4px; color:var(--dc-course-text)!important; border-radius:999px; font-weight:700; text-decoration:none!important; }
  .dc-course-account:hover { background:#f2f6fb; }
  .dc-course-account svg { width:17px; height:17px; }
  .dc-course-avatar { display:grid; width:40px; height:40px; place-items:center; color:#fff; background:var(--dc-course-blue); border-radius:50%; font-weight:800; }

  .dc-course-quick-nav { display:flex; min-height:54px; align-items:stretch; justify-content:center; gap:10px; padding:0 24px; background:#fff; border-top:1px solid #edf1f5; box-shadow:0 5px 16px rgba(9,38,79,.045); }
  .dc-course-quick-nav a { position:relative; display:flex; min-height:54px; align-items:center; padding:0 18px; color:#526079!important; font-size:14px; font-weight:750; text-decoration:none!important; }
  .dc-course-quick-nav a.active { color:var(--dc-course-blue)!important; }
  .dc-course-quick-nav a.active::after { content:''; position:absolute; right:15px; bottom:0; left:15px; height:3px; background:var(--dc-course-blue); border-radius:3px 3px 0 0; }
  .dc-course-quick-nav a:hover { color:var(--dc-course-blue)!important; background:#f6f9ff; }

  #courseTabsNavigation { display:none!important; }
  #courseTabsNavigation .container-xl { max-width:1480px; }
  #courseTabsNavigation .nav-bar { min-height:54px; align-items:stretch; }
  #courseTabsNavigation .nav-menu { display:flex; justify-content:center; }
  #courseTabsNavigation .nav { gap:14px; flex-wrap:nowrap; }
  #courseTabsNavigation .nav-link { position:relative; display:flex; min-height:54px; align-items:center; padding:0 18px; color:#526079; border:0!important; font-size:14px; font-weight:700; }
  #courseTabsNavigation .nav-link:hover { color:var(--dc-course-blue); background:#f6f9ff!important; }
  #courseTabsNavigation .nav-link.active { color:var(--dc-course-blue); background:transparent; }
  #courseTabsNavigation .nav-link.active::after { content:''; position:absolute; right:14px; bottom:0; left:14px; height:3px; background:var(--dc-course-blue); border-radius:3px 3px 0 0; }
  #courseTabsNavigation .search-toggle { display:flex; align-items:center; margin:0; }

  #main-content.container-xl { width:min(100% - 48px,1480px); max-width:none; margin-top:22px; padding:0; }
  .sequence-container { gap:20px; align-items:flex-start; margin-bottom:40px; }
  .sequence { min-width:0; overflow:visible; background:transparent; border:0!important; border-radius:0!important; }

  .outline-sidebar-wrapper { width:330px!important; max-width:330px; flex:0 0 330px!important; margin-right:0!important; overflow:visible!important; background:transparent!important; }
  .outline-sidebar { position:sticky!important; top:14px!important; left:auto!important; display:flex; max-height:calc(100vh - 28px); flex-direction:column; overflow-y:auto; padding:0 3px 8px; scrollbar-width:thin; scrollbar-color:#bdc8d7 transparent; }
  .dc-outline-progress { order:-3; margin:0 0 12px; padding:18px 20px; background:#fff; border:1px solid var(--dc-course-line); border-radius:11px; box-shadow:0 7px 24px rgba(9,38,79,.07); }
  .dc-outline-progress h2 { margin:0 0 15px; color:var(--dc-course-text); font-size:16px; font-weight:800; }
  .dc-outline-progress-body { display:flex; align-items:center; gap:18px; }
  .dc-progress-ring { display:grid; width:90px; height:90px; place-items:center; flex:0 0 auto; background:conic-gradient(var(--dc-course-blue) var(--dc-course-progress-angle),#e6ebf2 0); border-radius:50%; }
  .dc-progress-ring::before { content:''; grid-area:1/1; width:72px; height:72px; background:#fff; border-radius:50%; }
  .dc-progress-ring > span { z-index:1; grid-area:1/1; display:flex; align-items:center; flex-direction:column; }
  .dc-progress-ring strong { color:var(--dc-course-blue); font-size:22px; line-height:1; }
  .dc-progress-ring small { margin-top:4px; color:#607089; font-size:11px; font-weight:700; }
  .dc-progress-copy { display:flex; min-width:0; flex-direction:column; gap:10px; }
  .dc-progress-copy strong { color:#3d4d66; font-size:13px; line-height:1.45; }
  .dc-progress-copy a { color:var(--dc-course-blue); font-size:13px; font-weight:750; text-decoration:none; }

  .outline-sidebar-heading-wrapper { order:-2; position:relative!important; top:auto!important; min-height:62px; padding:12px 14px!important; color:var(--dc-course-text); background:#fff!important; border:1px solid var(--dc-course-line)!important; border-radius:11px; box-shadow:0 6px 20px rgba(9,38,79,.06); }
  .outline-sidebar-heading-wrapper::after { content:''; position:absolute; bottom:0; left:14px; width:42px; height:3px; background:var(--dc-course-gold); border-radius:3px 3px 0 0; }
  .outline-sidebar-heading { color:var(--dc-course-text)!important; font-size:15px!important; font-weight:800!important; }
  .outline-sidebar-toggle-btn { width:40px!important; height:40px!important; color:var(--dc-course-navy)!important; background:#f1f5fa!important; border-radius:8px!important; }

  #outline-sidebar-outline { order:-1; margin:10px 0 0!important; counter-reset:dc-course-unit; }
  #outline-sidebar-outline > li { counter-increment:dc-course-unit; position:relative; margin:0 0 8px!important; }
  #outline-sidebar-outline .course-sidebar-section,
  #outline-sidebar-outline .pgn__collapsible { overflow:hidden; margin:0!important; background:#fff; border:1px solid var(--dc-course-line)!important; border-radius:10px!important; box-shadow:0 4px 14px rgba(9,38,79,.045); }
  #outline-sidebar-outline .course-sidebar-section > button,
  #outline-sidebar-outline .collapsible-trigger { position:relative; min-height:70px; padding:13px 14px!important; color:var(--dc-course-text)!important; background:#fff; border:0; border-radius:10px!important; }
  #outline-sidebar-outline .course-sidebar-section > button::before,
  #outline-sidebar-outline .collapsible-trigger::before { content:counter(dc-course-unit); display:grid; width:26px; height:26px; place-items:center; flex:0 0 auto; margin-right:12px; color:#fff; background:#68758a; border-radius:50%; font-size:12px; font-weight:800; }
  #outline-sidebar-outline .course-sidebar-section > button > .col-auto:first-child,
  #outline-sidebar-outline .collapsible-trigger > .col-auto:first-child { display:none; }
  #outline-sidebar-outline .course-sidebar-section > button:hover,
  #outline-sidebar-outline .collapsible-trigger:hover { background:#f7f9fd!important; }
  #outline-sidebar-outline .bg-info-100,
  #outline-sidebar-outline .course-sidebar-section > button.bg-info-100 { color:var(--dc-course-navy)!important; background:#eaf2ff!important; box-shadow:inset 4px 0 0 var(--dc-course-gold); }
  #outline-sidebar-outline .bg-info-100::before { background:var(--dc-course-blue); }
  #outline-sidebar-outline .bg-info-100 span,
  #outline-sidebar-outline .bg-info-100 button,
  #outline-sidebar-outline .bg-info-100 a { color:var(--dc-course-navy)!important; font-weight:750; }
  #outline-sidebar-outline .collapsible-body { padding:0 8px 8px!important; background:#f8fafd; }
  #outline-sidebar-outline .collapsible-body ol { overflow:hidden; margin:0; border:1px solid #e6ebf2; border-radius:8px; }
  #outline-sidebar-outline .collapsible-body ol li > a { min-height:50px; padding:11px 13px!important; color:var(--dc-course-text)!important; background:#fff; text-decoration:none; }
  #outline-sidebar-outline .collapsible-body ol li > a:hover { background:#f4f8ff!important; }

  .sequence-navigation-container { position:sticky; z-index:20; top:8px; display:block; margin:0 0 16px; padding:12px; background:rgba(255,255,255,.96); border:1px solid var(--dc-course-line); border-radius:12px; box-shadow:0 8px 24px rgba(9,38,79,.08); backdrop-filter:blur(8px); }
  .dc-lesson-navigation { display:grid; grid-template-columns:minmax(180px,1fr) minmax(180px,.75fr) minmax(180px,1fr); align-items:center; gap:16px; width:100%; }
  .dc-lesson-nav-button { display:inline-flex; min-height:44px; align-items:center; gap:9px; padding:0 18px; border-radius:8px; font-size:14px; font-weight:800; text-decoration:none!important; cursor:pointer; transition:background-color .15s ease,border-color .15s ease,transform .15s ease; }
  .dc-lesson-nav-button svg { width:18px; height:18px; }
  .dc-lesson-nav-button.previous { justify-content:flex-start; color:var(--dc-course-navy); background:#fff; border:1px solid #b8c6da; }
  .dc-lesson-nav-button.previous:hover { background:#f3f7fd; border-color:var(--dc-course-blue); transform:translateY(-1px); }
  .dc-lesson-nav-button.next { justify-content:flex-end; color:#09264f; background:linear-gradient(135deg,var(--dc-course-gold-bright),var(--dc-course-gold)); border:1px solid var(--dc-course-gold); box-shadow:0 6px 15px rgba(227,161,26,.2); }
  .dc-lesson-nav-button.next:hover { background:#f2ae16; transform:translateY(-1px); }
  .dc-lesson-nav-button.is-disabled { opacity:.45; cursor:not-allowed; pointer-events:none; }
  .dc-lesson-position { display:flex; align-items:center; flex-direction:column; gap:7px; color:var(--dc-course-text); font-size:12px; text-align:center; }
  .dc-lesson-progress { display:block; width:100%; height:6px; overflow:hidden; background:#e3e8ef; border-radius:999px; }
  .dc-lesson-progress span { display:block; height:100%; background:var(--dc-course-blue); border-radius:inherit; transition:width .25s ease; }

  .unit-container { max-width:1120px!important; margin:0 auto; padding:24px 26px 30px!important; background:#fff; border:1px solid var(--dc-course-line); border-radius:12px; box-shadow:0 10px 32px rgba(9,38,79,.07); }
  .unit > div:first-child { align-items:flex-start; gap:20px; margin-bottom:2px; padding:0 2px 12px; border-bottom:1px solid #e8edf3; }
  .unit h3.h3 { margin:0 0 4px; color:var(--dc-course-text); font-size:22px; font-weight:800; line-height:1.3; letter-spacing:-.015em; }
  .unit > p .btn-link,
  .unit .btn-link { color:var(--dc-course-blue); font-weight:700; }
  .top-unit-navigation,
  .unit-navigation { display:none!important; }
  .unit-iframe-wrapper { overflow:hidden; margin:18px 0 0!important; background:#fff; border:1px solid #e1e7ef; border-radius:11px; box-shadow:0 7px 22px rgba(9,38,79,.055); }
  #unit-iframe { display:block; width:100%; border:0; }

  @media (max-width:1199.98px) {
    #main-content.container-xl { width:min(100% - 32px,1200px); }
    .outline-sidebar-wrapper { width:300px!important; max-width:300px; flex-basis:300px!important; }
    .dc-lesson-navigation { grid-template-columns:1fr auto 1fr; }
    .dc-lesson-position { min-width:150px; }
  }

  @media (max-width:991.98px) {
    .dc-course-header-inner { width:calc(100% - 32px); }
    .dc-course-logo { width:150px; }
    .dc-course-logo img { max-width:145px; }
    .dc-course-identity { max-width:360px; }
    #courseTabsNavigation .nav-menu { justify-content:flex-start; overflow-x:auto; }
    .sequence-container { display:block!important; }
    .outline-sidebar-wrapper { max-width:100%; }
    .sequence-navigation-container { top:6px; }
  }

  @media (max-width:767.98px) {
    .dc-course-header-inner { min-height:64px; }
    .dc-course-header-divider { height:32px; margin:0 12px; }
    .dc-course-identity span { display:none; }
    .dc-course-identity strong { max-width:180px; font-size:13px; }
    .dc-course-account > span:not(.dc-course-avatar) { display:none; }
    .dc-course-account svg { display:none; }
    .dc-course-quick-nav { justify-content:flex-start; overflow-x:auto; padding:0 10px; scrollbar-width:thin; }
    .dc-course-quick-nav a { flex:0 0 auto; padding:0 13px; font-size:13px; }
    #main-content.container-xl { width:calc(100% - 20px); margin-top:10px; }
    .sequence-navigation-container { position:sticky; top:auto; bottom:8px; margin-bottom:12px; padding:8px; }
    .dc-lesson-navigation { grid-template-columns:1fr 1fr; gap:8px; }
    .dc-lesson-position { display:none; }
    .dc-lesson-nav-button { min-height:46px; justify-content:center!important; padding:0 10px; font-size:13px; }
    .unit-container { padding:18px 14px 24px!important; border-radius:10px; }
    .unit h3.h3 { font-size:19px; }
    .unit-iframe-wrapper { border-right:0; border-left:0; border-radius:8px; }
  }

  @media (max-width:480px) {
    .dc-course-logo { width:126px; }
    .dc-course-logo img { max-width:122px; }
    .dc-course-header-divider,
    .dc-course-identity { display:none; }
    #courseTabsNavigation .nav-link { padding:0 12px; font-size:13px; }
  }

  @media (prefers-reduced-motion:reduce) {
    .dc-lesson-nav-button,
    .dc-lesson-progress span { transition:none; }
  }

  #footer.tutor-container,.wrapper-footer,#footer-openedx { display:none!important; }
  .dc-course-footer-minimal { display:flex; min-height:112px; align-items:center; justify-content:center; margin-top:56px; background:linear-gradient(115deg,#031d42 0%,#062957 68%,#0b49a6 100%); border-top:3px solid var(--dc-course-gold-bright); }
  .dc-course-footer-minimal a { display:inline-flex; align-items:center; justify-content:center; padding:8px 12px; background:#fff; border-radius:8px; box-shadow:0 10px 24px rgba(0,0,0,.16); }
  .dc-course-footer-minimal img { display:block; width:auto; max-width:148px; max-height:42px; object-fit:contain; }

  .dc-course-footer { margin-top:56px; color:#fff; background:#041f4e; border-top:3px solid var(--dc-course-gold-bright); }
  .dc-course-footer-main { display:grid; width:min(100% - 64px,1480px); grid-template-columns:minmax(300px,1.45fr) repeat(3,minmax(145px,.55fr)); gap:52px; margin:0 auto; padding:48px 0 38px; }
  .dc-course-footer-logo { display:inline-flex; padding:8px 12px; background:#fff; border-radius:8px; }
  .dc-course-footer-logo img { display:block; width:auto; max-width:170px; max-height:46px; object-fit:contain; }
  .dc-course-footer-brand>p,.dc-course-footer-column p { max-width:390px; margin:18px 0 14px; color:#bdcce0; font-size:14px; line-height:1.65; }
  .dc-course-footer-social { display:flex; flex-wrap:wrap; gap:14px; }
  .dc-course-footer-social a,.dc-course-footer-column a,.dc-course-footer-bottom a { color:#c8d6e7!important; font-size:13px; text-decoration:none!important; }
  .dc-course-footer-social a:hover,.dc-course-footer-column a:hover,.dc-course-footer-bottom a:hover { color:var(--dc-course-gold-bright)!important; }
  .dc-course-footer-column { display:flex; align-items:flex-start; flex-direction:column; gap:10px; }
  .dc-course-footer-column h2 { margin:3px 0 8px; color:var(--dc-course-gold-bright); font-size:13px; letter-spacing:.08em; text-transform:uppercase; }
  .dc-course-footer-column a.dc-course-footer-demo { display:inline-flex; min-height:40px; align-items:center; padding:0 15px; color:#061f4e!important; background:#ffbd2e!important; border-radius:6px; font-weight:850; opacity:1; text-shadow:none!important; }
  .dc-course-footer-bottom { display:flex; width:min(100% - 64px,1480px); min-height:64px; align-items:center; justify-content:space-between; gap:20px; margin:0 auto; color:#aebfd5; border-top:1px solid rgba(255,255,255,.12); font-size:12px; }
  @media (max-width:900px) { .dc-course-footer-main { grid-template-columns:1.2fr repeat(2,1fr); }.dc-course-footer-column:last-child { grid-column:2; } }
  @media (max-width:660px) { .dc-course-footer-main { width:calc(100% - 36px); grid-template-columns:1fr; gap:28px; }.dc-course-footer-column:last-child { grid-column:auto; }.dc-course-footer-bottom { width:calc(100% - 36px); align-items:flex-start; flex-direction:column; justify-content:center; padding:18px 0; } }
`;

// DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3_2026
{
  const { getAuthenticatedHttpClient: dcFeedbackHttpClient } = await import('@edx/frontend-platform/auth');
  const { getConfig: dcFeedbackGetConfig } = await import('@edx/frontend-platform');

  (() => {
    if (window.__DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3__) return;
    window.__DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3__ = true;

    const BUILD = 'DARACORP_DASHBOARD_ALIGNMENT_FEEDBACK_V3_2026';
    const API_PATH = '/api/daracorp/v1/course-feedback/';
    const style = document.createElement('style');
    style.id = 'daracorp-dashboard-alignment-feedback-v1-style';
    style.dataset.build = BUILD;
    style.textContent = `
      :root { --dc-feedback-shell:1420px; --dc-feedback-gutter:40px; --dc-feedback-navy:#072d65; --dc-feedback-blue:#0d5ee8; --dc-feedback-gold:#ffb81c; --dc-feedback-ink:#062653; --dc-feedback-muted:#60718c; --dc-feedback-line:#dbe3ee; --dc-feedback-pale:#eaf3ff; }
      .dc-header-inner,
      #dashboard-content > .dc-dashboard-hero,
      #dashboard-content > .container-mw-xl {
        width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell))!important;
        max-width:var(--dc-feedback-shell)!important;
        margin-right:auto!important;
        margin-left:auto!important;
      }
      #dashboard-content > .container-mw-xl { padding-right:0!important; padding-left:0!important; }
      #dashboard-content .course-list-column,
      #dashboard-content .course-list-container,
      #dashboard-content .course-list-heading-container,
      #dashboard-content .dc-dashboard-content { width:100%!important; max-width:none!important; margin-right:0!important; margin-left:0!important; }
      #dashboard-content .course-list-heading-container { padding-right:0!important; padding-left:0!important; }
      .dc-site-header .dc-header-logo { margin-left:0!important; }
      .dc-site-header .dc-utility-nav { margin-right:0!important; }
      .dc-feedback-completion-v1 { display:flex; width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell)); align-items:center; gap:14px; margin:0 auto 23px; padding:15px 18px; color:var(--dc-feedback-ink); background:var(--dc-feedback-pale); border:1px solid #bfd4ef; border-radius:10px; }
      .dc-feedback-completion-icon-v1 { display:grid; width:38px; height:38px; place-items:center; flex:0 0 auto; color:#fff; background:var(--dc-feedback-blue); border-radius:50%; font-size:20px; }
      .dc-feedback-completion-copy-v1 { min-width:0; }
      .dc-feedback-completion-copy-v1 strong { display:block; margin-bottom:2px; font-weight:760; }
      .dc-feedback-completion-copy-v1 span { color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-open-v1 { min-height:40px; margin-left:auto; padding:0 16px; color:#fff; background:var(--dc-feedback-blue); border:0; border-radius:7px; font-weight:760; cursor:pointer; }
      .dc-feedback-open-v1:hover { background:#084fca; }
      .dc-feedback-card-action-v1 { min-height:34px; padding:0 13px; color:var(--dc-feedback-blue); background:#fff; border:1px solid #9eb9dc; border-radius:6px; font-size:12px; font-weight:750; cursor:pointer; }
      .dc-feedback-card-action-v1:hover { background:#eef5ff; }
      .dc-feedback-course-cta-v1 { display:flex; width:min(calc(100% - var(--dc-feedback-gutter)),var(--dc-feedback-shell)); align-items:center; justify-content:space-between; gap:18px; margin:34px auto; padding:22px 24px; color:var(--dc-feedback-ink); background:#fff; border:1px solid var(--dc-feedback-line); border-radius:12px; box-shadow:0 8px 24px rgba(7,45,101,.08); }
      .dc-feedback-course-cta-v1 h2 { margin:0 0 5px; color:var(--dc-feedback-ink); font-size:20px; }
      .dc-feedback-course-cta-v1 p { margin:0; color:var(--dc-feedback-muted); }
      .dc-feedback-modal-backdrop-v1 { position:fixed; inset:0; z-index:2147483500; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(2,25,60,.62); backdrop-filter:blur(3px); }
      .dc-feedback-modal-v1 { width:min(100%,540px); overflow:hidden; color:var(--dc-feedback-ink); background:#fff; border:1px solid var(--dc-feedback-line); border-radius:14px; box-shadow:0 28px 80px rgba(0,25,64,.34); }
      .dc-feedback-modal-v1::before { content:''; display:block; height:6px; background:linear-gradient(90deg,var(--dc-feedback-gold) 0 34%,var(--dc-feedback-blue) 34% 100%); }
      .dc-feedback-modal-head-v1 { display:flex; align-items:flex-start; justify-content:space-between; gap:18px; padding:24px 25px 10px; }
      .dc-feedback-modal-head-v1 h2 { margin:0 0 5px; color:var(--dc-feedback-ink); font-size:24px; }
      .dc-feedback-modal-head-v1 p { margin:0; color:var(--dc-feedback-muted); font-size:14px; line-height:1.5; }
      .dc-feedback-close-v1 { display:grid; width:34px; height:34px; place-items:center; flex:0 0 auto; color:#52647c; background:#f2f5f9; border:0; border-radius:50%; font-size:21px; cursor:pointer; }
      .dc-feedback-form-v1 { padding:12px 25px 25px; }
      .dc-feedback-label-v1 { display:block; margin:0 0 8px; font-size:13px; font-weight:750; }
      .dc-feedback-stars-v1 { display:flex; gap:8px; margin-bottom:20px; }
      .dc-feedback-star-v1 { display:grid; width:44px; height:44px; place-items:center; color:#9aa8ba; background:#f6f8fb; border:1px solid #d6deea; border-radius:9px; font-size:25px; cursor:pointer; }
      .dc-feedback-star-v1.is-selected { color:#fff; background:var(--dc-feedback-gold); border-color:var(--dc-feedback-gold); }
      .dc-feedback-form-v1 textarea { width:100%; min-height:120px; resize:vertical; padding:12px 13px; color:var(--dc-feedback-ink); background:#fff; border:1px solid #bdc9d9; border-radius:8px; font:inherit; line-height:1.5; }
      .dc-feedback-check-v1 { display:flex; align-items:center; gap:8px; margin:13px 0 18px; color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-form-actions-v1 { display:flex; align-items:center; justify-content:flex-end; gap:10px; }
      .dc-feedback-cancel-v1,.dc-feedback-submit-v1 { min-height:42px; padding:0 17px; border-radius:7px; font-weight:750; cursor:pointer; }
      .dc-feedback-cancel-v1 { color:var(--dc-feedback-ink); background:#fff; border:1px solid #bdc9d9; }
      .dc-feedback-submit-v1 { color:#fff; background:var(--dc-feedback-blue); border:1px solid var(--dc-feedback-blue); }
      .dc-feedback-submit-v1:disabled { cursor:not-allowed; opacity:.55; }
      .dc-feedback-status-v1 { min-height:20px; margin:0 auto 0 0; color:var(--dc-feedback-muted); font-size:13px; }
      .dc-feedback-submit-v1.is-saving { position:relative; padding-left:42px; }
      .dc-feedback-submit-v1.is-saving::before { content:''; position:absolute; left:17px; width:14px; height:14px; border:2px solid rgba(255,255,255,.45); border-top-color:#fff; border-radius:50%; animation:dc-feedback-spin-v2 .7s linear infinite; }
      .dc-feedback-success-v2 { display:grid; min-height:390px; place-items:center; padding:42px 32px 36px; text-align:center; }
      .dc-feedback-success-icon-v2 { display:grid; width:84px; height:84px; place-items:center; margin:0 auto 22px; color:#fff; background:linear-gradient(145deg,#0b61e7,#1b7cff); border:8px solid #eaf3ff; border-radius:50%; box-shadow:0 14px 34px rgba(13,94,232,.24); font-size:40px; font-weight:850; }
      .dc-feedback-success-v2 h2 { margin:0 0 10px; color:var(--dc-feedback-ink); font-size:28px; }
      .dc-feedback-success-v2 p { max-width:360px; margin:0 auto 25px; color:var(--dc-feedback-muted); line-height:1.55; }
      .dc-feedback-done-v2 { min-width:150px; min-height:44px; color:#fff; background:var(--dc-feedback-blue); border:0; border-radius:8px; font-weight:780; cursor:pointer; }
      @keyframes dc-feedback-spin-v2 { to { transform:rotate(360deg); } }
      @media (max-width:768px) { :root { --dc-feedback-gutter:28px; } .dc-feedback-completion-v1,.dc-feedback-course-cta-v1 { align-items:flex-start; flex-direction:column; } .dc-feedback-open-v1 { width:100%; margin-left:0; } }
    `;
    document.head.appendChild(style);

    const apiUrl = () => `${String(dcFeedbackGetConfig().LMS_BASE_URL || '').replace(/\/$/,'')}${API_PATH}`;
    const absoluteCourseId = value => {
      const decoded = decodeURIComponent(String(value || ''));
      const match = decoded.match(/course-v1:[^/?#]+/);
      return match ? match[0] : '';
    };
    const courseFromCard = card => {
      const link = card.querySelector('.dc-card-title-row h3 a, h3 a, a[href*="course-v1:"]');
      return { courseId:absoluteCourseId(link?.href), courseName:(link?.textContent || 'this course').trim() };
    };
    const isCompleteCard = card => {
      const text = (card.textContent || '').replace(/\s+/g,' ');
      const fill = card.querySelector('.dc-progress-track > span');
      const width = parseFloat(fill?.style?.width || '0');
      return /100%\s*complete/i.test(text) || width >= 100;
    };

    const closeModal = () => {
      document.querySelector('.dc-feedback-modal-backdrop-v1')?.remove();
      document.body.style.removeProperty('overflow');
    };

    const openModal = async (courseId, courseName) => {
      closeModal();
      let rating = 0;
      const backdrop = document.createElement('div');
      backdrop.className = 'dc-feedback-modal-backdrop-v1';
      backdrop.innerHTML = `
        <section class="dc-feedback-modal-v1" role="dialog" aria-modal="true" aria-labelledby="dc-feedback-title-v1">
          <div class="dc-feedback-modal-head-v1"><div><h2 id="dc-feedback-title-v1">How was your course?</h2><p class="dc-feedback-course-name-v1"></p></div><button type="button" class="dc-feedback-close-v1" aria-label="Close">×</button></div>
          <form class="dc-feedback-form-v1">
            <span class="dc-feedback-label-v1">Overall experience</span>
            <div class="dc-feedback-stars-v1" role="radiogroup" aria-label="Course rating"></div>
            <label class="dc-feedback-label-v1" for="dc-feedback-comment-v1">What worked well, or what should we improve? <span style="font-weight:400">(optional)</span></label>
            <textarea id="dc-feedback-comment-v1" maxlength="2000" placeholder="Share a short comment"></textarea>
            <label class="dc-feedback-check-v1"><input type="checkbox" name="anonymous"> Submit my comment anonymously</label>
            <div class="dc-feedback-form-actions-v1"><span class="dc-feedback-status-v1" aria-live="polite"></span><button type="button" class="dc-feedback-cancel-v1">Cancel</button><button type="submit" class="dc-feedback-submit-v1" disabled>Submit feedback</button></div>
          </form>
        </section>`;
      backdrop.querySelector('.dc-feedback-course-name-v1').textContent = courseName || 'Course feedback';
      const stars = backdrop.querySelector('.dc-feedback-stars-v1');
      const submit = backdrop.querySelector('.dc-feedback-submit-v1');
      const renderStars = () => stars.querySelectorAll('button').forEach((button,index) => {
        const selected = index < rating;
        button.classList.toggle('is-selected',selected);
        button.setAttribute('aria-checked',String(index + 1 === rating));
      });
      for (let value=1; value<=5; value += 1) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'dc-feedback-star-v1'; button.textContent = '★';
        button.setAttribute('role','radio'); button.setAttribute('aria-label',`${value} star${value === 1 ? '' : 's'}`);
        button.addEventListener('click',() => { rating=value; renderStars(); submit.disabled=false; });
        stars.appendChild(button);
      }
      renderStars();
      backdrop.addEventListener('click',event => { if (event.target === backdrop) closeModal(); });
      backdrop.querySelector('.dc-feedback-close-v1').addEventListener('click',closeModal);
      backdrop.querySelector('.dc-feedback-cancel-v1').addEventListener('click',closeModal);
      document.addEventListener('keydown',function escapeFeedback(event){ if(event.key === 'Escape'){ document.removeEventListener('keydown',escapeFeedback); closeModal(); } });
      document.body.appendChild(backdrop); document.body.style.overflow='hidden';
      backdrop.querySelector('.dc-feedback-close-v1').focus();

      if (courseId) {
        try {
          const response = await dcFeedbackHttpClient().get(`${apiUrl()}?course_id=${encodeURIComponent(courseId)}`);
          const existing = response?.data?.feedback;
          if (existing) {
            rating = Number(existing.rating || 0);
            backdrop.querySelector('textarea').value = existing.comment || '';
            backdrop.querySelector('[name="anonymous"]').checked = Boolean(existing.anonymous);
            submit.disabled = rating < 1; renderStars();
          }
        } catch (error) { /* New feedback has no existing record. */ }
      }

      backdrop.querySelector('form').addEventListener('submit',async event => {
        event.preventDefault();
        const status = backdrop.querySelector('.dc-feedback-status-v1');
        if (rating < 1) { status.textContent='Choose a star rating before submitting.'; return; }
        if (!courseId) { status.textContent='We could not identify this course. Refresh the page and try again.'; return; }
        submit.disabled=true;
        submit.classList.add('is-saving');
        submit.textContent='Submitting…';
        status.textContent='Saving securely…';
        backdrop.querySelector('form').setAttribute('aria-busy','true');
        try {
          const response = await dcFeedbackHttpClient().post(apiUrl(),{
            course_id:courseId,
            course_name:courseName || '',
            rating,
            comment:backdrop.querySelector('textarea').value.trim(),
            anonymous:backdrop.querySelector('[name="anonymous"]').checked,
          });
          if (response?.data?.saved !== true) throw new Error('Feedback service did not confirm the save.');
          localStorage.setItem(`daracorp-feedback:${courseId}`,'submitted');
          const modal = backdrop.querySelector('.dc-feedback-modal-v1');
          modal.innerHTML = `<div class="dc-feedback-success-v2" role="status"><div><span class="dc-feedback-success-icon-v2" aria-hidden="true">✓</span><h2>Feedback submitted</h2><p>Thank you. Your response has been saved and will help us improve this course.</p><button type="button" class="dc-feedback-done-v2">Done</button></div></div>`;
          modal.querySelector('.dc-feedback-done-v2').addEventListener('click',closeModal);
          modal.querySelector('.dc-feedback-done-v2').focus();
          document.querySelectorAll('.dc-feedback-card-action-v1,.dc-feedback-open-v1').forEach(button => {
            if ((button.textContent || '').match(/feedback/i)) button.textContent='Feedback submitted ✓';
          });
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message || '';
          console.error('Daracorp feedback submission failed', error);
          status.textContent=detail ? `Not submitted: ${detail}` : 'We could not save your feedback. Please try again.';
          submit.disabled=false;
          submit.classList.remove('is-saving');
          submit.textContent='Submit feedback';
          backdrop.querySelector('form').removeAttribute('aria-busy');
        }
      });
    };

    const enhanceDashboard = () => {
      if (!/\/learner-dashboard\/?/.test(window.location.pathname)) return;
      const cards = Array.from(document.querySelectorAll('.dc-learning-card'));
      const completed = cards.filter(isCompleteCard);
      completed.forEach(card => {
        if (card.querySelector('.dc-feedback-card-action-v1')) return;
        const info = courseFromCard(card); if (!info.courseId) return;
        const footer = card.querySelector('.dc-card-footer'); if (!footer) return;
        const button = document.createElement('button'); button.type='button'; button.className='dc-feedback-card-action-v1'; button.textContent='Give feedback';
        button.addEventListener('click',() => openModal(info.courseId,info.courseName)); footer.appendChild(button);
      });
      if (!completed.length && new URLSearchParams(window.location.search).get('previewFeedback') !== '1') return;
      if (document.querySelector('.dc-feedback-completion-v1')) return;
      const info = completed.length ? courseFromCard(completed[0]) : courseFromCard(cards[0]);
      if (!info.courseId) return;
      const banner = document.createElement('section'); banner.className='dc-feedback-completion-v1';
      banner.innerHTML='<span class="dc-feedback-completion-icon-v1" aria-hidden="true">✓</span><div class="dc-feedback-completion-copy-v1"><strong>Course completed — congratulations.</strong><span>Tell us what helped and what we can improve.</span></div><button type="button" class="dc-feedback-open-v1">Share course feedback</button>';
      banner.querySelector('button').addEventListener('click',() => openModal(info.courseId,info.courseName));
      const hero = document.querySelector('#dashboard-content > .dc-dashboard-hero');
      if (hero) hero.insertAdjacentElement('afterend',banner);
    };

    const enhanceLearning = () => {
      const match = decodeURIComponent(window.location.pathname).match(/\/learning\/course\/(course-v1:[^/]+)\/home\/?$/);
      if (!match || document.querySelector('.dc-feedback-course-cta-v1')) return;
      const main = document.querySelector('main,#main'); if (!main) return;
      const courseId = match[1];
      const title = (document.querySelector('header h1,header h2,main h1')?.textContent || 'this course').trim();
      const card = document.createElement('section'); card.className='dc-feedback-course-cta-v1';
      card.innerHTML='<div><h2>Finished this course?</h2><p>Your feedback helps us strengthen the learning experience.</p></div><button type="button" class="dc-feedback-open-v1">Share course feedback</button>';
      card.querySelector('button').addEventListener('click',() => openModal(courseId,title)); main.appendChild(card);
    };

    const enhance = () => { enhanceDashboard(); enhanceLearning(); };
    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(document.documentElement,{childList:true,subtree:true});
  })();
}

// DARACORP_COURSE_TOUR_CARD_V1
// DARACORP_COURSE_GUIDE_V2
(function daracorpCourseTourCardV1() {
  if (window.__DARACORP_COURSE_TOUR_CARD_V1__) return;
  window.__DARACORP_COURSE_TOUR_CARD_V1__ = true;

  var style = document.createElement('style');
  style.id = 'daracorp-course-tour-card-v1-style';
  style.textContent = `
    #courseHome-launchTourLink.dc-tour-card-v1 {
      list-style: none !important;
      margin-top: 14px !important;
      padding-top: 14px !important;
      border-top: 1px solid #d9e3ef !important;
    }
    #courseHome-launchTourLink.dc-tour-card-v1 button {
      display: flex !important;
      align-items: center !important;
      gap: 10px !important;
      width: 100% !important;
      min-height: 46px !important;
      padding: 10px 13px !important;
      border: 1px solid #9db7d5 !important;
      border-radius: 9px !important;
      background: linear-gradient(135deg, #f7fbff 0%, #eaf3ff 100%) !important;
      color: #06295c !important;
      font-size: 14px !important;
      font-weight: 700 !important;
      line-height: 1.25 !important;
      text-align: left !important;
      text-decoration: none !important;
      box-shadow: 0 5px 14px rgba(5, 43, 92, .08) !important;
      transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease !important;
    }
    #courseHome-launchTourLink.dc-tour-card-v1 button:hover,
    #courseHome-launchTourLink.dc-tour-card-v1 button:focus-visible {
      border-color: #1165d8 !important;
      box-shadow: 0 8px 20px rgba(17, 101, 216, .16) !important;
      transform: translateY(-1px) !important;
    }
    #courseHome-launchTourLink.dc-tour-card-v1 button::before {
      content: '✦';
      display: grid;
      place-items: center;
      flex: 0 0 31px;
      width: 31px;
      height: 31px;
      border-radius: 50%;
      background: #0e5ee8;
      color: #fff;
      font-size: 15px;
      box-shadow: 0 4px 10px rgba(14, 94, 232, .24);
    }
    #courseHome-launchTourLink.dc-tour-card-v1::after {
      content: 'See where to find lessons, progress, and course tools.';
      display: block;
      margin: 7px 4px 0 4px;
      color: #52677f;
      font-size: 12px;
      line-height: 1.4;
    }
    body.dc-course-guide-open-v2 { overflow: hidden !important; }
    .dc-course-guide-backdrop-v2 {
      position: fixed;
      inset: 0;
      z-index: 2147483600;
      background: rgba(1, 20, 49, .48);
      backdrop-filter: blur(2px);
      animation: dcGuideFadeV2 .18s ease-out both;
    }
    .dc-course-guide-v2 {
      position: fixed;
      left: 50%;
      top: 50%;
      z-index: 2147483602;
      width: min(470px, calc(100vw - 32px));
      max-height: calc(100vh - 32px);
      overflow: auto;
      transform: translate(-50%, -50%);
      border: 1px solid rgba(172, 194, 220, .88);
      border-radius: 18px;
      background: #fff;
      color: #092d5b;
      box-shadow: 0 28px 80px rgba(0, 24, 63, .32);
      animation: dcGuideEnterV2 .22s cubic-bezier(.2,.8,.2,1) both;
    }
    .dc-course-guide-v2::before {
      content: '';
      display: block;
      height: 7px;
      background: linear-gradient(90deg, #f3ad16, #ffd66c 42%, #1464e9 42%, #1464e9);
    }
    .dc-course-guide-head-v2 {
      display: flex;
      align-items: flex-start;
      gap: 13px;
      padding: 24px 24px 10px;
    }
    .dc-course-guide-icon-v2 {
      display: grid;
      place-items: center;
      flex: 0 0 43px;
      width: 43px;
      height: 43px;
      border-radius: 13px;
      background: linear-gradient(145deg, #eaf4ff, #dceaff);
      color: #0d5fe9;
      font-size: 22px;
      box-shadow: inset 0 0 0 1px rgba(20,100,233,.12);
    }
    .dc-course-guide-kicker-v2 {
      margin: 0 0 4px;
      color: #0d5fe9;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: .12em;
      text-transform: uppercase;
    }
    .dc-course-guide-title-v2 {
      margin: 0;
      color: #082c58;
      font-size: 24px;
      font-weight: 800;
      line-height: 1.2;
    }
    .dc-course-guide-copy-v2 {
      min-height: 84px;
      margin: 0;
      padding: 5px 24px 20px;
      color: #415a74;
      font-size: 15px;
      line-height: 1.62;
    }
    .dc-course-guide-progress-v2 {
      display: flex;
      gap: 7px;
      padding: 0 24px 19px;
    }
    .dc-course-guide-dot-v2 {
      height: 5px;
      flex: 1 1 0;
      border: 0;
      border-radius: 99px;
      background: #dce5ef;
      padding: 0;
    }
    .dc-course-guide-dot-v2.is-active { background: #1261e8; }
    .dc-course-guide-actions-v2 {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 18px 24px 22px;
      border-top: 1px solid #e2e9f1;
      background: #f8fbff;
    }
    .dc-course-guide-actions-right-v2 { display: flex; gap: 9px; }
    .dc-course-guide-button-v2 {
      min-height: 42px;
      padding: 9px 17px;
      border: 1px solid #a9bad0;
      border-radius: 9px;
      background: #fff;
      color: #163b68;
      font: inherit;
      font-size: 14px;
      font-weight: 750;
      cursor: pointer;
    }
    .dc-course-guide-button-v2:hover,
    .dc-course-guide-button-v2:focus-visible {
      border-color: #1261e8;
      outline: 3px solid rgba(18,97,232,.15);
    }
    .dc-course-guide-button-v2.is-primary {
      border-color: #e2a014;
      background: linear-gradient(135deg, #f4ae19, #ffc846);
      color: #08264c;
      box-shadow: 0 7px 18px rgba(225,154,12,.22);
    }
    .dc-course-guide-target-v2 {
      position: relative !important;
      z-index: 2147483601 !important;
      border-radius: 10px !important;
      outline: 4px solid #f7b51d !important;
      outline-offset: 5px !important;
      box-shadow: 0 0 0 10px rgba(247,181,29,.18) !important;
    }
    @keyframes dcGuideFadeV2 { from { opacity: 0; } to { opacity: 1; } }
    @keyframes dcGuideEnterV2 {
      from { opacity: 0; transform: translate(-50%, calc(-50% + 12px)) scale(.98); }
      to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    @media (max-width: 560px) {
      .dc-course-guide-head-v2 { padding: 20px 18px 9px; }
      .dc-course-guide-copy-v2 { padding: 4px 18px 17px; }
      .dc-course-guide-progress-v2 { padding: 0 18px 16px; }
      .dc-course-guide-actions-v2 { padding: 15px 18px 18px; }
      .dc-course-guide-title-v2 { font-size: 21px; }
    }
  `;
  document.head.appendChild(style);

  var guideState = { step: 0, overlay: null, card: null, target: null };
  var guideSteps = [
    {
      title: 'Welcome to your course',
      copy: 'This short guide shows you where to continue learning, browse every unit, review progress, and find useful course tools.',
      target: function () { return findByText('button, a', /resume course|begin course/i); },
    },
    {
      title: 'Browse the course outline',
      copy: 'Open any unit to see its lessons, interactive tools, and quiz. Completed items stay marked so you always know where to continue.',
      target: function () { return findByText('h2, h3, strong, div', /unit 0?1:/i); },
    },
    {
      title: 'Review progress and dates',
      copy: 'Use the navigation above the course to check progress, important dates, refreshment activities, and certificates.',
      target: function () { return findByText('a, button', /^progress$/i); },
    },
    {
      title: 'Use your course tools',
      copy: 'Bookmark useful pages and reopen this guide whenever you need a quick reminder. You are ready to continue learning.',
      target: function () { return document.getElementById('courseHome-launchTourLink'); },
    },
  ];

  function findByText(selector, pattern) {
    var nodes = document.querySelectorAll(selector);
    for (var index = 0; index < nodes.length; index += 1) {
      var value = (nodes[index].textContent || '').replace(/\s+/g, ' ').trim();
      if (pattern.test(value)) return nodes[index];
    }
    return null;
  }

  function clearGuideTarget() {
    if (guideState.target) guideState.target.classList.remove('dc-course-guide-target-v2');
    guideState.target = null;
  }

  function closeDaracorpGuide() {
    clearGuideTarget();
    if (guideState.overlay) guideState.overlay.remove();
    if (guideState.card) guideState.card.remove();
    guideState.overlay = null;
    guideState.card = null;
    document.body.classList.remove('dc-course-guide-open-v2');
  }

  function renderDaracorpGuide() {
    if (!guideState.card) return;
    var step = guideSteps[guideState.step];
    guideState.card.querySelector('.dc-course-guide-kicker-v2').textContent =
      'Course guide - Step ' + (guideState.step + 1) + ' of ' + guideSteps.length;
    guideState.card.querySelector('.dc-course-guide-title-v2').textContent = step.title;
    guideState.card.querySelector('.dc-course-guide-copy-v2').textContent = step.copy;
    guideState.card.querySelector('[data-dc-guide-back]').disabled = guideState.step === 0;
    guideState.card.querySelector('[data-dc-guide-next]').textContent =
      guideState.step === guideSteps.length - 1 ? 'Finish' : 'Next';
    var dots = guideState.card.querySelectorAll('.dc-course-guide-dot-v2');
    for (var index = 0; index < dots.length; index += 1) {
      dots[index].classList.toggle('is-active', index <= guideState.step);
    }
    clearGuideTarget();
    var target = step.target && step.target();
    if (target && !guideState.card.contains(target)) {
      guideState.target = target;
      target.classList.add('dc-course-guide-target-v2');
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }

  function openDaracorpGuide() {
    closeDaracorpGuide();
    guideState.step = 0;
    guideState.overlay = document.createElement('div');
    guideState.overlay.className = 'dc-course-guide-backdrop-v2';
    guideState.overlay.setAttribute('aria-hidden', 'true');
    guideState.card = document.createElement('section');
    guideState.card.className = 'dc-course-guide-v2';
    guideState.card.setAttribute('role', 'dialog');
    guideState.card.setAttribute('aria-modal', 'true');
    guideState.card.setAttribute('aria-label', 'Daracorp guided course tour');
    guideState.card.innerHTML =
      '<div class="dc-course-guide-head-v2">' +
        '<span class="dc-course-guide-icon-v2" aria-hidden="true">&#10022;</span>' +
        '<div><p class="dc-course-guide-kicker-v2"></p><h2 class="dc-course-guide-title-v2"></h2></div>' +
      '</div>' +
      '<p class="dc-course-guide-copy-v2"></p>' +
      '<div class="dc-course-guide-progress-v2" aria-hidden="true">' +
        guideSteps.map(function () { return '<span class="dc-course-guide-dot-v2"></span>'; }).join('') +
      '</div>' +
      '<div class="dc-course-guide-actions-v2">' +
        '<button class="dc-course-guide-button-v2" type="button" data-dc-guide-close>Dismiss</button>' +
        '<div class="dc-course-guide-actions-right-v2">' +
          '<button class="dc-course-guide-button-v2" type="button" data-dc-guide-back>Back</button>' +
          '<button class="dc-course-guide-button-v2 is-primary" type="button" data-dc-guide-next>Next</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(guideState.overlay);
    document.body.appendChild(guideState.card);
    document.body.classList.add('dc-course-guide-open-v2');
    guideState.overlay.addEventListener('click', closeDaracorpGuide);
    guideState.card.querySelector('[data-dc-guide-close]').addEventListener('click', closeDaracorpGuide);
    guideState.card.querySelector('[data-dc-guide-back]').addEventListener('click', function () {
      if (guideState.step > 0) guideState.step -= 1;
      renderDaracorpGuide();
    });
    guideState.card.querySelector('[data-dc-guide-next]').addEventListener('click', function () {
      if (guideState.step >= guideSteps.length - 1) {
        closeDaracorpGuide();
        return;
      }
      guideState.step += 1;
      renderDaracorpGuide();
    });
    renderDaracorpGuide();
    guideState.card.querySelector('[data-dc-guide-next]').focus();
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && guideState.card) closeDaracorpGuide();
  });

  function enhanceCourseTour() {
    var item = document.getElementById('courseHome-launchTourLink');
    if (!item) return;
    item.classList.add('dc-tour-card-v1');
    var button = item.querySelector('button');
    if (!button) return;
    button.setAttribute('aria-label', 'Take a guided course tour');
    if (!button.dataset.dcGuideV2Bound) {
      button.dataset.dcGuideV2Bound = 'true';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openDaracorpGuide();
      }, true);
    }
    var walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    var textNode;
    while ((textNode = walker.nextNode())) {
      if (/launch\s+tour/i.test(textNode.nodeValue || '')) {
        textNode.nodeValue = 'Take a guided course tour';
        break;
      }
    }
  }

  enhanceCourseTour();
  new MutationObserver(enhanceCourseTour).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
const { ConfigurableAIAssistance } = await import("@openedx/openedx-ai-extensions-ui");
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_course_header_v2',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpLearningHeader,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.learning.course_outline_sidebar.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_course_chrome_v2',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpCourseChrome,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.learning.sequence_navigation.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_sequence_navigation_v2',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpSequenceNavigation,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [{ op: PLUGIN_OPERATIONS.Hide, widgetId: 'default_contents' }]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'daracorp_course_footer_v2',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: DaracorpMinimalCourseFooter,
              },
            }
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'theme_switch_button',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
      addPlugins(config, 'org.openedx.frontend.learning.unit_title.v1', [
          {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'ai-assist-button',
                priority: 10,
                type: DIRECT_PLUGIN,
                RenderWidget: ConfigurableAIAssistance,
            },
          }]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
      addPlugins(config, 'logo_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                },
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'custom_logo',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ThemedLogo,
                    }
                }
            ]);
    }
/* DARACORP_AUTHN_V7_REFERENCE_MATCH_2026 */
if (process.env.APP_ID === 'authn' && !window.__daracorpAuthnV7) {
  window.__daracorpAuthnV7 = true;
  const { getConfig: dcGetConfig } = await import('@edx/frontend-platform');

  const dcCss = `
    :root {
      --dc6-navy: #031d49;
      --dc6-mid: #073571;
      --dc6-blue: #115de0;
      --dc6-action: #0b61e7;
      --dc6-action-bright: #187cff;
      --dc6-gold: #f5b31b;
      --dc6-ink: #062657;
      --dc6-muted: #63718a;
      --dc6-line: #d6e1ef;
      --dc6-canvas-gutter: max(2rem, calc((100vw - 103.75rem) / 2 + 5.75rem));
    }

    html.dc-authn-v6-login,
    html.dc-authn-v6-login body,
    html.dc-authn-v6-login #root {
      width: 100%;
      min-height: 100%;
    }

    html.dc-authn-v6-login body {
      margin: 0 !important;
      overflow-x: hidden !important;
      color: var(--dc6-ink) !important;
      background: var(--dc6-navy) !important;
    }

    html.dc-authn-v6-login .extra-large-screen-top-stripe,
    html.dc-authn-v6-login #controlled-tab,
    html.dc-authn-v6-login #main-content > h3 {
      display: none !important;
    }

    html.dc-authn-v6-login .layout {
      position: relative !important;
      display: block !important;
      width: 100% !important;
      min-height: 100vh !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      background: transparent !important;
    }

    html.dc-authn-v6-login .layout > :not(.content):not(.dc-authn-v6-brand) {
      display: none !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-brand {
      position: fixed;
      z-index: 1;
      inset: 0;
      overflow: hidden;
      color: #fff;
      background:
        radial-gradient(circle at 77% 18%, rgba(31, 113, 247, .46), transparent 31rem),
        radial-gradient(circle at 17% 94%, rgba(14, 91, 221, .27), transparent 26rem),
        linear-gradient(118deg, var(--dc6-navy) 0%, var(--dc6-mid) 54%, var(--dc6-blue) 100%);
    }

    html.dc-authn-v6-login .dc-authn-v6-brand::before {
      content: '';
      position: absolute;
      inset: 0;
      opacity: .22;
      pointer-events: none;
      background-image: radial-gradient(rgba(107, 175, 255, .6) 1px, transparent 1px);
      background-size: 23px 23px;
      -webkit-mask-image: linear-gradient(90deg, #000, transparent 73%);
      mask-image: linear-gradient(90deg, #000, transparent 73%);
    }

    html.dc-authn-v6-login .dc-authn-v6-brand::after {
      content: '';
      position: absolute;
      right: -14rem;
      bottom: -18rem;
      width: 44rem;
      height: 44rem;
      border: 1px solid rgba(255,255,255,.12);
      border-radius: 50%;
      box-shadow: 0 0 0 5rem rgba(255,255,255,.021), 0 0 0 10rem rgba(255,255,255,.016);
      pointer-events: none;
    }

    html.dc-authn-v6-login .dc-authn-v6-brand-inner {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      width: min(60.75rem, calc(100vw - 43rem));
      min-height: 100vh;
      margin-left: 0;
      padding: clamp(3.9rem, 5.5vw, 5.9rem) 2rem clamp(3.9rem, 5.5vw, 5.9rem) var(--dc6-canvas-gutter);
    }

    html.dc-authn-v6-login .dc-authn-v6-logo {
      display: inline-flex;
      align-items: center;
      align-self: flex-start;
      width: auto;
      height: clamp(3.7rem, 4.5vw, 4.5rem);
      color: #fff;
      text-decoration: none;
    }

    html.dc-authn-v6-login .dc-authn-v6-logo img {
      display: block;
      width: auto;
      max-width: 21rem;
      height: 100%;
      object-fit: contain;
      object-position: left center;
    }

    html.dc-authn-v6-login .dc-authn-v6-logo-fallback {
      padding-bottom: .3rem;
      border-bottom: 4px solid var(--dc6-gold);
      color: #fff;
      font-size: 2.25rem;
      font-weight: 850;
      letter-spacing: -.025em;
    }

    html.dc-authn-v6-login .dc-authn-v6-copy {
      max-width: 47rem;
      margin-top: clamp(5.2rem, 11vh, 7.8rem);
    }

    html.dc-authn-v6-login .dc-authn-v6-kicker {
      margin: 0;
      color: var(--dc6-gold);
      font-size: 1.15rem;
      font-weight: 850;
      letter-spacing: .17em;
      text-transform: uppercase;
    }

    html.dc-authn-v6-login .dc-authn-v6-kicker::after {
      content: '';
      display: block;
      width: 3.9rem;
      height: 4px;
      margin-top: 1rem;
      background: var(--dc6-gold);
      border-radius: 99px;
    }

    html.dc-authn-v6-login .dc-authn-v6-title {
      max-width: 48rem;
      margin: 1.6rem 0 0;
      color: #fff !important;
      font-size: clamp(3.75rem, 4.5vw, 4.65rem) !important;
      font-weight: 850 !important;
      letter-spacing: -.055em !important;
      line-height: 1.06 !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-title span { display: block; }

    html.dc-authn-v6-login .dc-authn-v6-message {
      max-width: 43rem;
      margin: 1.65rem 0 0;
      color: #dce9ff;
      font-size: clamp(1.14rem, 1.38vw, 1.34rem);
      line-height: 1.7;
    }

    html.dc-authn-v6-login .dc-authn-v6-features {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: .9rem;
      max-width: 47rem;
      margin: 2rem 0 0;
      padding: 0;
      list-style: none;
    }

    html.dc-authn-v6-login .dc-authn-v6-features li {
      display: flex;
      align-items: center;
      gap: .7rem;
      min-height: 4.55rem;
      padding: .75rem .95rem;
      color: #f5f8ff;
      background: rgba(3, 30, 72, .34);
      border: 1px solid rgba(147, 187, 239, .25);
      border-radius: .9rem;
      font-size: .91rem;
      font-weight: 760;
      line-height: 1.25;
    }

    html.dc-authn-v6-login .dc-authn-v6-feature-icon {
      display: grid;
      flex: 0 0 auto;
      place-items: center;
      width: 2.55rem;
      height: 2.55rem;
      color: #65d5ff;
      background: rgba(24,119,255,.16);
      border: 1px solid rgba(101, 213, 255, .25);
      border-radius: .68rem;
      font-size: 1.15rem;
    }

    html.dc-authn-v6-login .dc-authn-v6-features li:first-child .dc-authn-v6-feature-icon {
      color: #ffc43d;
      background: rgba(245,179,27,.13);
      border-color: rgba(245,179,27,.28);
    }

    html.dc-authn-v6-login .dc-authn-v6-features li:last-child .dc-authn-v6-feature-icon {
      color: #5ae0bf;
      background: rgba(50,205,168,.12);
      border-color: rgba(90,224,191,.27);
    }

    html.dc-authn-v6-login .dc-authn-v6-growth {
      position: absolute;
      z-index: 1;
      left: calc(var(--dc6-canvas-gutter) - .5rem);
      bottom: -1.5rem;
      width: min(54rem, 52vw);
      height: auto;
      opacity: .78;
      pointer-events: none;
    }

    html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-line-blue { filter:drop-shadow(0 0 8px rgba(45,145,255,.8)); }
    html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-line-gold { filter:drop-shadow(0 0 6px rgba(255,188,42,.55)); }
    html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-orb { transform-box:fill-box; transform-origin:center; animation:dc-authn-orb-v7 2.2s ease-in-out infinite; }
    html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-orb-gold { animation-delay:.75s; }
    html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-sheen { animation:dc-authn-sheen-v7 4.8s linear infinite; }

    html.dc-authn-v6-login .dc-authn-v6-copyright {
      margin-top: auto;
      color: #d2e2f8;
      font-size: .9rem;
    }

    html.dc-authn-v6-login .content {
      position: fixed !important;
      z-index: 10 !important;
      top: 50% !important;
      right: var(--dc6-canvas-gutter) !important;
      display: block !important;
      width: min(35rem, 37vw) !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      transform: translateY(-50%) !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    @keyframes dc-authn-orb-v7 {
      0%,100% { opacity:.72; transform:scale(.78); }
      50% { opacity:1; transform:scale(1.28); }
    }

    @keyframes dc-authn-sheen-v7 {
      from { stroke-dashoffset:0; }
      to { stroke-dashoffset:-96; }
    }

    html.dc-authn-v6-login .content > div {
      position: relative !important;
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      min-height: min(50rem, calc(100vh - 7rem)) !important;
      margin: 0 !important;
      padding: clamp(2.6rem, 4vh, 3.7rem) 2.9rem 6.25rem !important;
      overflow: hidden !important;
      color: var(--dc6-ink) !important;
      background: radial-gradient(circle at 82% 2%, rgba(52,130,255,.12), transparent 16rem), linear-gradient(145deg, rgba(255,255,255,.995), rgba(248,251,255,.985)) !important;
      border: 1px solid rgba(182,205,238,.9) !important;
      border-left: 4px solid var(--dc6-gold) !important;
      border-radius: 1.45rem !important;
      box-shadow: 0 40px 95px rgba(0,25,66,.38), 0 14px 34px rgba(26,105,238,.16), inset 0 1px 0 #fff !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    html.dc-authn-v6-login .content > div::before {
      content: '';
      position: absolute;
      z-index: 0;
      inset: 0 0 auto;
      height: 10rem;
      background: linear-gradient(135deg, rgba(255,255,255,.88), rgba(37,116,255,.075), transparent 78%);
      pointer-events: none;
    }

    html.dc-authn-v6-login .content > div::after {
      content: '🔒  Your connection is secure.' !important;
      position: absolute !important;
      z-index: 1 !important;
      right: 0 !important;
      bottom: 0 !important;
      left: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      height: 5.2rem !important;
      margin: 0 !important;
      padding: 0 1rem !important;
      color: #68768b !important;
      background: linear-gradient(180deg, #f9fbff, #f1f6fc) !important;
      border-top: 1px solid var(--dc6-line) !important;
      border-radius: 0 0 1.35rem 1.35rem !important;
      font-size: .95rem !important;
      font-weight: 650 !important;
      text-align: center !important;
    }

    html.dc-authn-v6-login .content > div > * {
      position: relative;
      z-index: 1;
    }

    html.dc-authn-v6-login #main-content.main-content,
    html.dc-authn-v6-login #main-content .mw-xs,
    html.dc-authn-v6-login #main-content .mw-420,
    html.dc-authn-v6-login #main-content .mw-500,
    html.dc-authn-v6-login #sign-in-form {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-intro {
      margin-bottom: 2rem;
      text-align: center;
    }

    html.dc-authn-v6-login .dc-authn-v6-shield {
      display: grid;
      place-items: center;
      width: 5.15rem;
      height: 5.15rem;
      margin: 0 auto 1.6rem;
      color: #1464ed;
      background: radial-gradient(circle at 35% 25%, #fff, #e8f2ff 72%);
      border: 1px solid rgba(58,132,255,.16);
      border-radius: 50%;
      box-shadow: 0 12px 30px rgba(21,104,238,.18), inset 0 1px 0 #fff;
    }

    html.dc-authn-v6-login .dc-authn-v6-shield svg {
      width: 3.25rem;
      height: 3.25rem;
      filter: drop-shadow(0 4px 7px rgba(18,101,232,.22));
    }

    html.dc-authn-v6-login .dc-authn-v6-intro h1 {
      margin: 0;
      color: var(--dc6-ink) !important;
      font-size: 2.5rem !important;
      font-weight: 850 !important;
      letter-spacing: -.04em !important;
      line-height: 1.08 !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-intro p {
      margin: .85rem 0 0;
      color: var(--dc6-muted);
      font-size: 1.05rem;
      line-height: 1.55;
    }

    html.dc-authn-v6-login #sign-in-form .pgn__form-group {
      display: block !important;
      margin-bottom: 1.35rem !important;
    }

    html.dc-authn-v6-login #sign-in-form label,
    html.dc-authn-v6-login #sign-in-form .pgn__form-control-floating-label-content {
      color: var(--dc6-ink) !important;
      font-weight: 750 !important;
    }

    html.dc-authn-v6-login #sign-in-form .form-control {
      min-height: 4.15rem !important;
      padding-right: 3.75rem !important;
      color: var(--dc6-ink) !important;
      background: linear-gradient(180deg,#fff,#f9fbff) !important;
      border: 1px solid #c7d6ea !important;
      border-radius: .82rem !important;
      box-shadow: inset 0 1px 2px rgba(7,42,92,.045), 0 3px 10px rgba(17,85,183,.035) !important;
      transition: border-color .2s ease, box-shadow .2s ease, background .2s ease !important;
    }

    html.dc-authn-v6-login #sign-in-form .form-control:focus {
      background-color: #fff !important;
      border-color: #1971f5 !important;
      box-shadow: 0 0 0 .22rem rgba(25,113,245,.13), 0 8px 22px rgba(16,91,207,.08) !important;
    }

    html.dc-authn-v6-login #emailOrUsername,
    html.dc-authn-v6-login #sign-in-form input[name='emailOrUsername'],
    html.dc-authn-v6-login #sign-in-form input[name='username'] {
      padding-left: 3.65rem !important;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 32 32' fill='none'%3E%3Ccircle cx='16' cy='10' r='6' stroke='%23758296' stroke-width='2.5'/%3E%3Cpath d='M6 29v-3c0-5.4 4.5-9 10-9s10 3.6 10 9v3' stroke='%23758296' stroke-width='2.5' stroke-linecap='round'/%3E%3C/svg%3E"), linear-gradient(180deg,#fff,#f9fbff) !important;
      background-repeat: no-repeat, no-repeat !important;
      background-position: 1.15rem center, center !important;
      background-size: 1.8rem 1.8rem, 100% 100% !important;
    }

    html.dc-authn-v6-login #password,
    html.dc-authn-v6-login #sign-in-form input[name='password'] {
      padding-left: 3.65rem !important;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 32 32' fill='none'%3E%3Crect x='7' y='13' width='18' height='15' rx='3' stroke='%23758296' stroke-width='2.5'/%3E%3Cpath d='M11 13V9a5 5 0 0 1 10 0v4M16 19v4' stroke='%23758296' stroke-width='2.5' stroke-linecap='round'/%3E%3C/svg%3E"), linear-gradient(180deg,#fff,#f9fbff) !important;
      background-repeat: no-repeat, no-repeat !important;
      background-position: 1.15rem center, center !important;
      background-size: 1.8rem 1.8rem, 100% 100% !important;
    }

    html.dc-authn-v6-login #sign-in {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      min-height: 4.2rem !important;
      margin: .25rem 0 0 !important;
      overflow: hidden !important;
      color: #fff !important;
      background: linear-gradient(105deg,#064fd8 0%,#1479ff 48%,#053fc5 100%) !important;
      background-size: 180% 100% !important;
      border: 1px solid rgba(0,61,190,.38) !important;
      border-radius: .85rem !important;
      box-shadow: 0 16px 32px rgba(0,91,224,.33), inset 0 1px 0 rgba(255,255,255,.38), inset 0 -1px 0 rgba(0,36,126,.34) !important;
      font-size: 1.15rem !important;
      font-weight: 850 !important;
      transition: transform .18s ease, box-shadow .18s ease, background-position .35s ease !important;
    }

    html.dc-authn-v6-login #sign-in:hover,
    html.dc-authn-v6-login #sign-in:focus-visible {
      background-position: 100% 0 !important;
      box-shadow: 0 18px 36px rgba(0,91,224,.4), 0 0 0 .2rem rgba(35,124,255,.13), inset 0 1px 0 rgba(255,255,255,.5) !important;
      transform: translateY(-1px) !important;
    }

    html.dc-authn-v6-login #forgot-password {
      display: block !important;
      width: fit-content !important;
      margin: 1.2rem auto 0 !important;
      padding: .3rem .5rem !important;
      color: #0962e8 !important;
      font-size: 1.03rem !important;
      font-weight: 650 !important;
      text-decoration: none !important;
    }

    html.dc-authn-v6-login #forgot-password:hover { text-decoration: underline !important; }

    html.dc-authn-v6-login .dc-authn-v6-support {
      margin: .65rem 0 0 !important;
      color: #68768b !important;
      font-size: .82rem !important;
      line-height: 1.35 !important;
      text-align: center !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-support a {
      color: #1262e7 !important;
      font-weight: 750 !important;
      text-decoration: none !important;
    }

    html.dc-authn-v6-login .dc-authn-v6-support a:hover { text-decoration: underline !important; }

    html.dc-authn-v6-login #sign-in-form .btn-social,
    html.dc-authn-v6-login #sign-in-form .btn-tpa {
      width: 100% !important;
      min-width: 100% !important;
    }

    @media (min-width: 1200px) and (max-height: 790px) {
      html.dc-authn-v6-login .dc-authn-v6-brand-inner { padding-top: 2.5rem; padding-bottom: 2.5rem; }
      html.dc-authn-v6-login .dc-authn-v6-copy { margin-top: 2.7rem; }
      html.dc-authn-v6-login .dc-authn-v6-title { font-size: clamp(3rem, 4vw, 3.8rem) !important; }
      html.dc-authn-v6-login .content > div { min-height: calc(100vh - 3rem) !important; padding-top: 1.7rem !important; }
      html.dc-authn-v6-login .dc-authn-v6-intro { margin-bottom: 1.1rem; }
      html.dc-authn-v6-login .dc-authn-v6-shield { width: 4.2rem; height: 4.2rem; margin-bottom: .8rem; }
      html.dc-authn-v6-login .dc-authn-v6-shield svg { width: 2.7rem; height: 2.7rem; }
    }

    @media (max-width: 1199.98px) {
      :root { --dc6-canvas-gutter: 2rem; }
      html.dc-authn-v6-login body { background: linear-gradient(145deg,#f7fbff,#eaf3fc) !important; }
      html.dc-authn-v6-login .layout { overflow: auto !important; padding: 1rem !important; }
      html.dc-authn-v6-login .dc-authn-v6-brand {
        position: relative;
        inset: auto;
        min-height: 24rem;
        border-radius: 1rem;
      }
      html.dc-authn-v6-login .dc-authn-v6-brand-inner { width: 100%; min-height: 24rem; padding: 2rem; }
      html.dc-authn-v6-login .dc-authn-v6-copy { margin-top: 2.5rem; }
      html.dc-authn-v6-login .dc-authn-v6-title { font-size: clamp(2.5rem,7vw,3.8rem) !important; }
      html.dc-authn-v6-login .dc-authn-v6-growth,
      html.dc-authn-v6-login .dc-authn-v6-copyright { display: none; }
      html.dc-authn-v6-login .content {
        position: relative !important;
        top: auto !important;
        right: auto !important;
        width: min(100%,35rem) !important;
        margin: -4rem auto 2rem !important;
        transform: none !important;
      }
      html.dc-authn-v6-login .content > div { min-height: 0 !important; }
    }

    @media (max-width: 700px) {
      html.dc-authn-v6-login .layout { padding: 0 !important; }
      html.dc-authn-v6-login .dc-authn-v6-brand { border-radius: 0; }
      html.dc-authn-v6-login .dc-authn-v6-brand-inner { min-height: 19rem; padding: 1.4rem; }
      html.dc-authn-v6-login .dc-authn-v6-logo { height: 2.8rem; }
      html.dc-authn-v6-login .dc-authn-v6-copy { margin-top: 1.8rem; }
      html.dc-authn-v6-login .dc-authn-v6-kicker,
      html.dc-authn-v6-login .dc-authn-v6-message,
      html.dc-authn-v6-login .dc-authn-v6-features { display: none; }
      html.dc-authn-v6-login .dc-authn-v6-title { font-size: 2.35rem !important; }
      html.dc-authn-v6-login .content { width: 100% !important; margin: 0 !important; }
      html.dc-authn-v6-login .content > div { padding: 2rem 1.3rem 5.7rem !important; border: 0 !important; border-radius: 0 !important; }
      html.dc-authn-v6-login .dc-authn-v6-intro h1 { font-size: 2rem !important; }
    }

    @media (prefers-reduced-motion: reduce) {
      html.dc-authn-v6-login #sign-in,
      html.dc-authn-v6-login #sign-in-form .form-control { transition: none !important; }
      html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-orb,
      html.dc-authn-v6-login .dc-authn-v6-growth .dc-growth-sheen { animation:none !important; }
    }
  `;

  const dcStyle = document.createElement('style');
  dcStyle.id = 'daracorp-authn-v7-style';
  dcStyle.textContent = dcCss;
  document.head.appendChild(dcStyle);

  const dcIsLogin = () => /\/authn\/login\/?$/.test(window.location.pathname);

  const dcCreateBrand = () => {
    const cfg = dcGetConfig();
    const brand = document.createElement('aside');
    brand.className = 'dc-authn-v6-brand';
    brand.setAttribute('aria-labelledby', 'dc-authn-v6-title');
    brand.innerHTML = `
      <div class="dc-authn-v6-brand-inner">
        <a class="dc-authn-v6-logo" aria-label="Daracorp home"><span class="dc-authn-v6-logo-fallback">Daracorp</span></a>
        <div class="dc-authn-v6-copy">
          <p class="dc-authn-v6-kicker">Daracorp Learning</p>
          <h2 class="dc-authn-v6-title" id="dc-authn-v6-title"><span>Build skills.</span><span>Grow with confidence.</span></h2>
          <p class="dc-authn-v6-message">Practical learning for stronger decisions, better compliance, and lasting professional growth.</p>
          <ul class="dc-authn-v6-features" aria-label="Learning benefits">
            <li><span class="dc-authn-v6-feature-icon">◷</span><span>Learn at your pace</span></li>
            <li><span class="dc-authn-v6-feature-icon">▥</span><span>Track your progress</span></li>
            <li><span class="dc-authn-v6-feature-icon">▣</span><span>Earn certificates</span></li>
          </ul>
        </div>
        <p class="dc-authn-v6-copyright">© ${new Date().getFullYear()} Daracorp</p>
      </div>
      <svg class="dc-authn-v6-growth" viewBox="0 0 760 290" fill="none" aria-hidden="true">
        <defs>
          <linearGradient id="dc-growth-gradient-v7" x1="10" y1="270" x2="740" y2="0" gradientUnits="userSpaceOnUse"><stop stop-color="#1763d4"/><stop offset=".58" stop-color="#2a8fff"/><stop offset="1" stop-color="#4ca7ff"/></linearGradient>
          <radialGradient id="dc-growth-blue-orb-v7"><stop stop-color="#fff"/><stop offset=".25" stop-color="#a9dcff"/><stop offset=".65" stop-color="#2c9fff"/><stop offset="1" stop-color="#2c9fff" stop-opacity="0"/></radialGradient>
          <radialGradient id="dc-growth-gold-orb-v7"><stop stop-color="#fff"/><stop offset=".25" stop-color="#ffe7a0"/><stop offset=".65" stop-color="#ffc638"/><stop offset="1" stop-color="#ffc638" stop-opacity="0"/></radialGradient>
        </defs>
        <path class="dc-growth-line-blue" d="M10 270C125 245 195 249 292 210S430 162 512 93s135-45 228-140" stroke="url(#dc-growth-gradient-v7)" stroke-width="4"/>
        <path class="dc-growth-sheen" d="M10 270C125 245 195 249 292 210S430 162 512 93s135-45 228-140" stroke="rgba(255,255,255,.8)" stroke-width="1.5" stroke-dasharray="4 92"/>
        <path class="dc-growth-line-gold" d="M10 287C145 262 230 270 329 229s162-66 225-132 119-60 190-118" stroke="#efb323" stroke-width="1.6" stroke-dasharray="5 7"/>
        <circle class="dc-growth-orb" cx="292" cy="210" r="19" fill="url(#dc-growth-blue-orb-v7)"/><circle class="dc-growth-orb dc-growth-orb-gold" cx="512" cy="93" r="19" fill="url(#dc-growth-gold-orb-v7)"/>
        <circle cx="292" cy="210" r="6" fill="#63c0ff"/><circle cx="512" cy="93" r="6" fill="#ffd04a"/>
        <path d="M410 270v-66h47v66M485 270V155h47v115M560 270V95h47v175M635 270V26h47v244" fill="#2e82ed" opacity=".18"/>
      </svg>`;
    const home = cfg.MARKETING_SITE_BASE_URL || cfg.LMS_BASE_URL || '/';
    const logo = cfg.LOGO_WHITE_URL || cfg.LOGO_URL || '';
    const link = brand.querySelector('.dc-authn-v6-logo');
    link.setAttribute('href', home);
    if (logo) {
      link.textContent = '';
      const img = document.createElement('img');
      img.src = logo;
      img.alt = 'Daracorp';
      link.appendChild(img);
    }
    return brand;
  };

  const dcCreateIntro = () => {
    const intro = document.createElement('section');
    intro.className = 'dc-authn-v6-intro';
    intro.innerHTML = `
      <span class="dc-authn-v6-shield" aria-hidden="true">
        <svg viewBox="0 0 48 48" fill="none"><path d="M24 4c6 5 12 6 17 7v12c0 10-6.8 17.3-17 21C13.8 40.3 7 33 7 23V11c5-1 11-2 17-7Z" stroke="currentColor" stroke-width="3"/><path d="m16 24 5.2 5.2L33 17.5" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
      <h1>Welcome back</h1>
      <p>Sign in to continue to Daracorp Learning.</p>`;
    return intro;
  };

  const dcEnsure = () => {
    const active = dcIsLogin();
    document.documentElement.classList.toggle('dc-authn-v6-login', active);
    if (!active) {
      document.querySelectorAll('.dc-authn-v6-brand,.dc-authn-v6-intro,.dc-authn-v6-support').forEach(node => node.remove());
      return;
    }
    const layout = document.querySelector('.layout');
    const content = layout && layout.querySelector(':scope > .content');
    const main = document.querySelector('#main-content');
    if (!layout || !content || !main) return;
    if (!layout.querySelector(':scope > .dc-authn-v6-brand')) layout.prepend(dcCreateBrand());
    if (!main.querySelector(':scope > .dc-authn-v6-intro')) main.prepend(dcCreateIntro());
    const forgot = main.querySelector('#forgot-password');
    if (forgot && !main.querySelector('.dc-authn-v6-support')) {
      const support = document.createElement('p');
      support.className = 'dc-authn-v6-support';
      support.append('Need help signing in? ');
      const email = document.createElement('a');
      email.href = 'mailto:contact@daracorp.io';
      email.textContent = 'Email contact@daracorp.io';
      support.appendChild(email);
      forgot.insertAdjacentElement('afterend', support);
    }
  };

  const dcObserver = new MutationObserver(dcEnsure);
  dcObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('popstate', dcEnsure);
  window.addEventListener('hashchange', dcEnsure);
  window.requestAnimationFrame(dcEnsure);
}

/* DARACORP_DASHBOARD_NAV_POLISH_V1_2026 */
if (process.env.APP_ID === 'learner-dashboard' && !window.__daracorpDashboardNavPolishV1) {
  window.__daracorpDashboardNavPolishV1 = true;
  document.documentElement.classList.add('dc-dashboard-nav-polish-v1');

  const dcDashboardNavCss = `
    @media (min-width: 1000px) {
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-header-inner {
        width: min(calc(100% - 4rem), 90rem) !important;
        min-height: 4.6rem !important;
        align-items: stretch !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-header-logo {
        width: 10.75rem !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-header-logo img {
        max-width: 10.25rem !important;
        max-height: 2.9rem !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav {
        display: flex !important;
        grid-template-columns: none !important;
        align-items: stretch !important;
        gap: .2rem !important;
        margin-left: auto !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a {
        min-width: 0 !important;
        padding: 0 .72rem !important;
        color: #34445f !important;
        border-radius: .55rem .55rem 0 0 !important;
        font-family: Inter, "Segoe UI", Arial, sans-serif !important;
        font-size: .96rem !important;
        font-weight: 660 !important;
        letter-spacing: -.012em !important;
        line-height: 1.2 !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a:hover,
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a:focus-visible {
        color: #0e5ce6 !important;
        background: #f3f7fd !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a.active {
        color: #0e5ce6 !important;
        font-weight: 720 !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a.active::after {
        right: .7rem !important;
        left: .7rem !important;
        height: 2.5px !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-utility-nav {
        gap: .55rem !important;
        margin-left: 1rem !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-icon-link {
        width: 2.55rem !important;
        height: 2.55rem !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-link {
        gap: .62rem !important;
        min-height: 2.75rem !important;
        padding: .28rem .38rem .28rem .2rem !important;
        border-radius: .75rem !important;
        font-family: Inter, "Segoe UI", Arial, sans-serif !important;
        font-size: .96rem !important;
        font-weight: 680 !important;
        letter-spacing: -.01em !important;
        line-height: 1.2 !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-link:hover,
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-link:focus-visible,
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-link[aria-expanded='true'] {
        background: #f2f6fc !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-avatar {
        width: 2.5rem !important;
        height: 2.5rem !important;
        font-family: Inter, "Segoe UI", Arial, sans-serif !important;
        font-size: .96rem !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-chevron {
        display: inline-block !important;
        width: .52rem !important;
        height: .52rem !important;
        margin: 0 .2rem .28rem .05rem !important;
        color: transparent !important;
        border-right: 2px solid #40516d !important;
        border-bottom: 2px solid #40516d !important;
        font-size: 0 !important;
        line-height: 0 !important;
        transform: rotate(45deg) !important;
        transform-origin: center !important;
        transition: transform .18s ease, margin .18s ease !important;
      }

      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-account-link[aria-expanded='true'] .dc-account-chevron {
        margin-top: .3rem !important;
        margin-bottom: 0 !important;
        transform: rotate(225deg) !important;
      }
    }

    @media (min-width: 1000px) and (max-width: 1260px) {
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-header-inner {
        width: calc(100% - 2rem) !important;
      }
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-primary-nav a {
        padding-right: .48rem !important;
        padding-left: .48rem !important;
        font-size: .88rem !important;
      }
      html.dc-dashboard-nav-polish-v1 .dc-site-header-desktop .dc-utility-nav {
        margin-left: .55rem !important;
      }
    }
  `;

  const dcDashboardNavStyle = document.createElement('style');
  dcDashboardNavStyle.id = 'daracorp-dashboard-nav-polish-v1-style';
  dcDashboardNavStyle.textContent = dcDashboardNavCss;
  document.head.appendChild(dcDashboardNavStyle);
}
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;